import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { liftStyle, liftTransition, useCenterLift, useExpandDrop } from '~/lib/use-center-lift';
import { scrollConversationToEnd } from './scroll-conversation';
import { isEmptyConversation, showStartLine } from './chat-start-line';
import { EditableChatTitle } from '~/features/chat/editable-chat-title';
import { useBackdropDismiss } from '~/hooks/use-backdrop-dismiss';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  X,
  CalendarPlus,
  Share,
  DownloadSimple,
  Brain,
  FileText,
  Trash,
  CircleNotch,
  Lightning,
  Paperclip,
  PencilSimple,
  Check,
  ArrowUUpLeft,
  ClipboardText,
  Plus,
  Folder,
  IdentificationBadge,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { EddyMark, EddyWordmark } from '~/components/eddy-mark';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import { ChatMessages } from '~/features/chat/chat-messages';
import { answerCardByText } from '~/lib/answer-card-by-text';
import { ChatInput, type ChatAttachment } from '~/features/chat/chat-input';
import type { VoiceTurn } from '~/features/voice/use-voice-call';
import { useStopAudioAnswerOnSurfaceChange } from '~/features/voice/use-stop-audio-answer';
import { PlanCard } from '~/features/chat/plan-card';
import { SaveProposalCard } from '~/features/chat/save-proposal-card';
import {
  SAVE_PROPOSAL_DECLINED_MESSAGE,
  saveProposalApprovedMessage,
  saveProposalCommentMessage,
} from '~/features/chat/save-proposal-message';
import { voiceCallBinding } from '~/features/chat/voice-call-binding';
import { persistCallEnded } from '~/features/voice/persist-call-ended';
import { useHandsFreeCall } from '~/features/voice/hands-free/hands-free-call';
import { ChatLamp } from '~/features/chat/chat-lamp';
import { ChatActivityMenu } from '~/features/chat/chat-activity-menu';
import { useUiRefactoring } from '~/lib/ui-refactoring';
import { CLEARED_ON_SWITCH } from '~/features/chat/switch-open-props';
import { ChatResearchBanner } from '~/features/chat/chat-research-banner';
import { ChatScopeChip } from '~/features/chat/chat-scope-chip';
import { ComposerChipRow } from '~/features/chat/chat-task-chips';
import { useChatTasks, useStopTaskAnswer } from '~/hooks/queries/use-chat-tasks';
import { isTaskResultPending, pendingResultTaskIds } from '~/features/chat/chat-tasks-view';
import { isServerTurnUnattached, useServerTurnPickup } from '~/features/chat/server-turn-pickup';
import { useChatScope, resolveEffectiveScope, type ChatScopeEntity } from '~/hooks/queries/use-chat-scope';
import type { ContainerPickerOption } from '~/features/work/container-picker';
import { isResearchBannerVisible } from '~/features/chat/research-banner-logic';
import { nextResearchPanelState } from '~/features/chat/research-panel-open';
import {
  shouldRefreshAfterResearch,
  historyContainsBriefingFor,
  nextResearchRefreshStep,
  RESEARCH_REFRESH_RETRY_DELAY_MS,
  RESEARCH_REFRESH_MAX_ATTEMPTS,
} from '~/features/chat/research-refresh-gate';
import { shouldClearOptimisticResearch } from '~/features/chat/research-optimistic-gate';
import { researchFinishedNotice } from '~/features/chat/research-finished-notice';
import { shouldPromoteOnClose } from '~/features/chat/promote-on-close-logic';
import { useAgentChat } from '~/hooks/use-agent-chat';
import { runningFallbackRunId } from '~/hooks/background-chat-stop';
import { isTurnStreaming, isQueueBusy } from '~/hooks/turn-busy';
import { useBackgroundChatForSession } from '~/hooks/queries/use-background-chats';
import { useDetachedBgChats } from '~/lib/stores/detached-bg-chats-store';
import { useReadBgChats } from '~/lib/stores/read-bg-chats-store';
import { useCloseOnOperatorPanelRequest } from '~/lib/stores/use-close-on-operator-panel-request';
import { useCloseOnProBuilderOpen } from '~/lib/stores/use-close-on-pro-builder-open';
import { assetWindowTitle } from '~/features/windows/window-titles';
import { WindowShell, useWindowChrome } from '~/features/windows/window-system';
import { WindowMinimizeButton } from '~/features/windows/window-minimize-button';
import { ChatWindowTitle } from './chat-window-title';
import { WindowPopOutButton } from '~/features/windows/window-pop-out-button';
import { useResearch } from '~/hooks/queries/use-research';
import { useMarkChatSeen, useChatStatuses } from '~/hooks/queries/use-chat-status';
import { useChatModelMode } from '~/hooks/queries/use-chat-model-mode';
import { useChatRole } from '~/hooks/queries/use-chat-role';
import { ChatRoleDialog } from '~/features/chat/chat-role-dialog';
import {
  useDocumentDetail,
  useDeleteDocument,
  useBulkDocuments,
  documentKeys,
} from '~/hooks/queries/use-documents';
import {
  useFileDetail,
  useUpdateFile,
  useDeleteFile,
  useBulkFiles,
  fileKeys,
} from '~/hooks/queries/use-files';
import { useConfirm } from '~/components/confirm-dialog';
import {
  feedKeys,
  chatKeys,
  saveProposalKeys,
  researchKeys,
  backgroundChatKeys,
  chatStatusKeys,
  filesOverviewKeys, attentionKeys } from '~/lib/query-keys';
import {
  useCreateDocument,
  useUpdateDocument,
} from '~/hooks/mutations/use-document-mutations';
import { useRecordOpen } from '~/hooks/mutations/use-record-open';
import {
  deriveTitle,
  conversationToMarkdown,
  deriveConversationTitle,
} from './conversation-markdown';
import { backdropMayClose, canSaveEdit, escapeAction } from './document-edit';
import { useMediaQuery } from '~/hooks/use-media-query';
import { ARTIFACT_WORKSPACE_FLAG } from '~/hooks/queries/use-feature-flags';
import { CanvasContext } from '~/features/canvas/canvas-context';
import { CanvasPanel } from '~/features/canvas/canvas-panel';
import { CANVAS_SEND_HOLD_HINT, gateSendOnCanvasDraft, type CanvasFlush } from '~/features/canvas/canvas-send-gate';
import { CanvasHeaderControls } from '~/features/canvas/canvas-header-controls';
import { useCanvasState } from '~/features/canvas/use-canvas-state';
import { activeArtifactForSend, artifactNamedBySend, type ActiveArtifactRef } from '~/features/canvas/active-artifact';
import {
  CANVAS_LAYOUT_CSS,
  canvasCardClass,
  canvasCardMaxWidth,
  isPanelVisible,
} from '~/features/canvas/canvas-layout';
import { deleteEverything } from './delete-everything';
import {
  useTrashChat,
  useRestoreChat,
  useResolveChatToGraph,
  useResolveChatToDocument,
  useSetChatScope,
} from '~/hooks/mutations/use-chat-mutations';
import { useChatThread } from '~/hooks/queries/use-chats';
import { api } from '~/lib/api-client';
import {
  parseResult,
  iconForTask,
  toIcsDate,
  downloadBlob,
} from '~/features/home/briefing-utils';
import {
  InboxWarningDialog,
  isWarningSuppressed,
  type WarningKey,
} from '~/features/chat/inbox-warning-dialog';
import {
  isFileAsset,
  isChatAsset,
  isReopenedFromThread,
  uploadDownloadMode,
  uploadHasTextBody,
  binaryAssetHasBody,
  rawFileUrl,
  fileAssetToContextAttachment,
  deleteDescriptorFor,
  derivePlanSourceForFooter,
  resolveDisplayTitle,
  type ModalAssetItem,
} from '~/features/chat/modal-asset';
import { resolveEditableAsset } from '~/features/chat/editable-asset';
import { CreatePlanDialog } from '~/features/chat/create-plan-dialog';
// FRONT-1251: a cron-generated item tells Eddy which scheduled job made it.
import { describeFeedItemSource } from '~/features/chat/asset-provenance';
import { MODEL_PICKER_COMPOSER_FLAG, useFeatureFlag } from '~/hooks/queries/use-feature-flags';
import {
  useReadingPrefs,
  // FRONT-3223: the header "Aa" of the `eddy.ui-refactoring` OFF path. Aliased
  // because `reading-settings-section.test.tsx` pins the new header without it.
  ReadingPrefsButton as LegacyReadingPrefsButton,
  LEGACY_READING_CARD_PX,
  LEGACY_READING_COLUMN_PX,
  READING_CARD_PX,
  READING_COLUMN_PX,
  READING_FONT_PX,
} from '~/features/chat/reading-prefs';
import {
  deriveContextFiles,
  loadRemovedFileIds,
  persistRemovedFileIds,
} from '~/features/chat/context-files';
import { showUndoToast } from '~/lib/undo-toast';
import { resolveDocumentSavedMessage } from '~/lib/document-save-outcome';
import { Tip } from '~/components/ui/tip';
import { formatDateTime, formatTime } from '~/lib/local-datetime';
import { ItemDates } from '~/components/item-dates';
import { ItemTags } from '~/features/files/item-tags';
import { resolveItemDates, selectLiveItemDates, type LiveItemDatesSource } from '~/lib/item-dates';
import { ChatQuestionsDockedContext, useChatQuestionDock } from './chat-question-dock';
import { useChatCardDock } from './chat-card-dock';

/** With the flag on the chat window is never closed by Pro Builder opening. */
function noopClose(): void {}

interface InboxChatModalProps {
  /**
   * The clicked asset. An inbox feed item (cron-run result or memory document),
   * a user-uploaded file (FRONT-290), or — since FRONT-518 — an anchor-less web
   * chat thread. The component is mounted only when one exists.
   */
  item: ModalAssetItem;
  onClose: () => void;
  /**
   * FRONT-3146: set by the window host (`features/windows/window-host.tsx`)
   * for a window it created from an open request. Absent for a page-local
   * mount, which registers its own window. Not an open prop: it says who owns
   * the window, not how the chat was opened, so a thread switch keeps it.
   */
  windowId?: string;
  /**
   * FRONT-1550: an alternative close that clears this modal's state WITHOUT
   * also performing the host's return-to-origin navigation. Two callers: an
   * in-chat control that navigates the app elsewhere (the Work scope chip,
   * the folder chip) as part of its own click, since that close IS itself a
   * deliberate navigation to somewhere else; and (FRONT-3003) the Pro Builder
   * window opening over this modal, where any navigation would hide Pro
   * Builder again. React Router 7's data router makes `navigate()` async, so a
   * "did we already leave Home?" guard read back at close time would race the
   * in-flight navigation; naming the two closes explicitly avoids that race
   * entirely. Falls back to `onClose` when the host doesn't need the
   * distinction (i.e. it navigates nowhere on its own close, so there's
   * nothing to protect against).
   */
  onCloseAfterNavigate?: () => void;
  /**
   * FRONT-518 (chat variant only): the first message to send once, on open —
   * the text typed in the launcher's "Start" composer. Delivered through the
   * modal's own session so the new thread is minted server-side here.
   */
  initialMessage?: string;
  initialAttachments?: ChatAttachment[];
  /**
   * FRONT-535 (chat variant only): image intent + model from the launcher's
   * image button, threaded into the first message so a brand-new chat can open
   * straight into an image generation.
   */
  initialImage?: { intent?: boolean; model?: string };
  /**
   * FRONT-531 (chat variant only): the modal was opened from the launcher's
   * phone button — start the live voice call straight away so the user lands
   * in the conversation by talking. Mutually exclusive with initialMessage.
   */
  initialVoiceCall?: boolean;
  /**
   * FRONT-1191 (chat variant only): the Work container this chat was JUST
   * minted about, from the board's/detail page's "Project chat about
   * ⟨name⟩" button (`ask-eddy-button.tsx`, renamed from "Ask Eddy" by
   * FRONT-1808) — `routes/work.tsx` and
   * `routes/work-detail.tsx` mount this modal locally and pass the
   * container's identity straight in (no cross-page store hop; see the
   * bug-fix note on their own `handleChat`). A fresh scoped chat already
   * knows its container, so this is fed to `resolveEffectiveScope`
   * (`use-chat-scope.ts`) as the SEED — it paints the marker instantly,
   * before `useChatScope` below has an answer of its own.
   *
   * FRONT-1813 finding 3 (corrected): `useChatScope` IS always called below,
   * seed or no seed — it used to be skipped for exactly this reason
   * (FRONT-1113's connection-budget concern: don't make the marker WAIT on
   * a fetch), but finding 1 showed that starving the query of an active
   * observer meant an already-scoped chat could never learn about a LATER
   * server-side move (Eddy's own `work_chat_move` tool has no client-side
   * mutation hook and relies entirely on the query being live for the
   * `chat-scope-changed` SSE invalidation to reach it). The original
   * concern — the marker must not visibly wait on a fetch — is still
   * honoured, just by a different mechanism: `resolveEffectiveScope` always
   * prefers this seed until the query's own result is `known: true`, so the
   * first paint is instant either way, and the fetch itself runs
   * unblocking in the background rather than being skipped. Absent for
   * every other open path (pill click, reopen, command palette), in which
   * case there is no seed and the marker simply waits for the query's first
   * `known` answer, same as before.
   */
  initialScope?: { entityId: string; name: string; type: 'project'; archived: boolean };
  /**
   * FRONT-776 (chat variant only): the Files-screen Folder this chat was
   * JUST minted about, from the folder detail page's "Ask Eddy about this
   * folder" button — mirrors `initialScope` exactly (same reasoning: a
   * fresh scoped chat already knows its folder, no fetch needed). Mutually
   * exclusive with `initialScope` in practice (a chat is scoped to a Work
   * container OR a folder, never both) — nothing enforces that here, the
   * two callers (`work-detail.tsx`/`work.tsx` vs. `folder-detail.tsx`) each
   * only ever pass their own kind.
   *
   * Unlike `initialScope`, there is no fetch-back-on-reopen path yet: a
   * reopened folder-scoped chat (e.g. via the Chats list) shows no "About:
   * <folder>" marker and does not resend `folderScopeId` — the backend's own
   * turn-then-stored precedence (reading `chat_sessions.scopeFolderId`
   * fresh when the turn doesn't supply one) is what keeps the folder's
   * content injected on every later turn regardless, so this is a purely
   * cosmetic gap (the marker chip), not a functional one.
   */
  initialFolderScope?: { folderId: string; name: string };
  /**
   * FRONT-2433 (chat variant only): run one of the resolution bar's keep
   * actions as soon as the conversation has loaded — the Chats page's row
   * menu opens the chat this way for "Save as document" and "Save to Graph",
   * so both go through exactly the in-chat flow (the conversation serialized
   * here, the Graph preview approved here) rather than a second copy.
   */
  initialAction?: 'save-document' | 'save-graph';
  /**
   * FRONT-1073 (chat variant only): starts a fresh chat without closing the
   * modal first. Owned by the caller, which decides what "fresh" mounts to
   * (a new `newChatAsset()`, remounted via the caller's own key).
   */
  onNewChat?: () => void;
}

interface InboxChatModalInnerProps extends InboxChatModalProps {
  /**
   * FRONT-1337: hand the modal onto another chat thread, chosen in the
   * modal's own chat-activity menu (FRONT-1579). Always supplied by the wrapper below, which owns the swap so the inner
   * body can remount cleanly on the new session — the same clean-slate the
   * launcher used to get by changing its own mount key.
   */
  onSwitchThread: (item: ModalAssetItem) => void;
}

/**
 * Opens an inbox asset in a centred "zen" modal (per the Inbox-Chat design):
 * a dimmed, blurred backdrop with a single tall card. The asset is rendered in
 * a comfortable reading column up top; an EDDY chat and the resolve actions sit
 * beneath it. Nothing fires on open — the conversation starts only when the
 * user sends a message, at which point the asset rides along as context
 * (injected server-side as a non-persisted system note) so the conversation is
 * about the asset while the user's bubbles stay clean.
 *
 * The chat is bound to a stable per-asset session id so it persists across
 * opens. Mount with `key={item.id}` so each asset gets a fresh instance.
 *
 * FRONT-204 — background contract: closing this modal (× / backdrop / Esc) ONLY
 * unmounts the component, which aborts the browser's SSE read. The server turn
 * keeps running to completion and still persists its answer, so a question
 * "continues in the background" by design. Never wire a server-side cancel into
 * onClose / an unmount effect — only the explicit Stop button (`stopRun`) may
 * cancel a turn. The Inspector card + inbox-row dot track the run from here on.
 */
/** FRONT-254: what this window shows, as the hands-free call names it. */
function openScreenItem(w: {
  isChat: boolean;
  sessionId: string;
  fileId: string | undefined;
  documentFilename: string | undefined;
}): { type: 'chat' | 'file' | 'document' | null; id: string | null } {
  if (w.isChat) return { type: 'chat', id: w.sessionId };
  if (w.fileId) return { type: 'file', id: w.fileId };
  if (w.documentFilename) return { type: 'document', id: w.documentFilename };
  return { type: null, id: null };
}

/**
 * FRONT-3223: values of the `eddy.ui-refactoring` OFF path (the window as it
 * was on main before FRONT-3142). Kept as constants because source-text tests
 * of the new window pin the literal new values and the absence of the old
 * ones (`new-build-columns.test.tsx`, `chat-top-bar.test.tsx`).
 */
const LEGACY_NEW_CHAT_LABEL = 'New chat';
const LEGACY_COLUMN_SIDE_PX = 32;
const LEGACY_COLUMN_PADDING = `44px ${LEGACY_COLUMN_SIDE_PX}px 28px`;
/** The old chat resolution bar: no rule, no fill, centred like the reworked one; only the tall minimum is dropped. */
const LEGACY_RESOLUTION_BAR_STYLE = {
  minHeight: 0,
} as const;

function InboxChatModalInner({
  item,
  onClose,
  onCloseAfterNavigate,
  initialMessage,
  initialAttachments,
  initialImage,
  initialVoiceCall,
  initialScope,
  initialFolderScope,
  initialAction,
  onSwitchThread,
  onNewChat,
}: InboxChatModalInnerProps) {
  const navigate = useNavigate();
  // FRONT-3146: docked in the window layer (minimize/close buttons, no scrim,
  // Escape minimizes) or the standalone full-screen overlay it always was.
  const chrome = useWindowChrome();
  // FRONT-3223: `eddy.ui-refactoring` (margins, editable title, started line,
  // mark, header controls, column padding, composer rule, resolution bar, the
  // lamp vs the "Chat activity" dropdown). Off is the window as it was on main.
  const v2 = useUiRefactoring();
  // Normalize the asset kinds: memory document, cron-run result (both arrive as
  // feed items), user-uploaded file (FRONT-290), and the anchor-less web chat
  // (FRONT-518). The chat variant has no asset to read — the conversation is the
  // whole content — so the reader, asset-context injection, and resolve footer
  // are all suppressed below.
  const chatItem = isChatAsset(item) ? item : null;
  const isChat = chatItem !== null;
  const fileAsset = isFileAsset(item) ? item : null;
  const feedItem = !isFileAsset(item) && !isChatAsset(item) ? item : null;
  const isFile = fileAsset !== null;
  const isDocument = feedItem?.type === 'document';
  const filename = isDocument ? feedItem?.filename : undefined;
  // FRONT-2391: record this open for the command palette's "Recent
  // knowledge" section — this modal renders a file/document asset without
  // ever mounting `files.tsx`'s own `FileDetail`/`DocumentDetail` (those
  // choke points cover only `/files/:id` and `/files/doc/:filename`), so it
  // needs its own pair of these. `undefined` for whichever branch doesn't
  // apply — each hook no-ops on `undefined`.
  useRecordOpen('file', fileAsset?.fileId);
  useRecordOpen('document', filename);
  // The chat variant binds to its raw `web:<uuid>` session; assets keep the
  // stable per-asset `inbox-<id>` session.
  const inboxSessionId = isChat ? chatItem.sessionId : `inbox-${item.id}`;
  // Lifecycle row of THIS conversation's thread (see the "In Recycle bin"
  // cue further down) — read here, above `useAgentChat`, because it is also
  // the fallback source for the direct-agent pin below.
  // FRONT-1804: `ownTurnLiveSince` mirrors when this window's own turn went
  // live (`isStreaming`, set below from useAgentChat — which needs `threadRow`'s
  // pin, hence the state hop; null = not live) so the row is polled, for a
  // bounded window, while the new chat is being named.
  const [ownTurnLiveSince, setOwnTurnLiveSince] = useState<number | null>(null);
  const { data: threadRow } = useChatThread(inboxSessionId, { turnLiveSince: ownTurnLiveSince });
  // FRONT-2522: the agent this chat is pinned to, if any. The ASSET wins
  // (an open request / list row that already knows it marks the window on
  // the first paint), and the server's own thread read is the fallback for
  // every opener that only knows a session id (the top-bar board, the
  // command palette, the inspector) — so a pinned chat is marked, and keeps
  // sending `directAgentId`, however it was reopened. Fresh sessions 404 to
  // null and ordinary chats answer null, so neither is ever marked.
  const directAgent = chatItem?.directAgent ?? threadRow?.directAgent ?? null;
  // FRONT-2387: this modal is the desktop "visible conversation" surface —
  // stop any answer reading itself out loud when it closes or switches to a
  // different session (see the hook's own docblock).
  useStopAudioAnswerOnSurfaceChange(inboxSessionId);
  // FRONT-568: the live voice call is available on every surface here (chat,
  // file, inbox feed item) and binds to this session, so its turns/recap/save
  // card land in this very conversation.
  const voiceBinding = voiceCallBinding(inboxSessionId, !!initialVoiceCall);
  // FRONT-820: same query the composer's Fast/Thoughtful pill reads — one
  // cache entry, so the header chip and the pill can never disagree.
  //
  // FRONT-1698: with the composer model picker on, Fast/Thoughtful no longer
  // decides the model (a pick does), so this chip would name a mode that is
  // ignored — it is neither fetched nor shown.
  const modelPickerOn = useFeatureFlag(MODEL_PICKER_COMPOSER_FLAG, false);
  const { data: headerModelMode } = useChatModelMode(isChat && !modelPickerOn ? inboxSessionId : undefined);
  // FRONT-1566: same pattern — the header Role pill and the dialog it opens
  // both read this one cache entry (`chatRoleKeys.detail`), so they can
  // never disagree either.
  //
  // adversarial-review finding 9: `isError` is also read here — a failed
  // GET leaves `chatRoleInstructions` `undefined`, same as "no role set",
  // so without this the pill claimed "Set a role for this conversation"
  // (implying none is set) for a conversation that HAS one, whenever the
  // read itself failed. `ChatRoleButton` uses it to say it doesn't know
  // rather than assert an absence it can't back up.
  const { data: chatRoleInstructions, isError: chatRoleErrored } = useChatRole(
    isChat ? inboxSessionId : undefined,
  );
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);

  // FRONT-1191: the Work container this chat is about, if any. A fresh scoped
  // chat already knows it (`initialScope`, handed down from the launcher's
  // pending-scope state) — passed as the SEED to `resolveEffectiveScope`
  // (`use-chat-scope.ts`) below, so the marker paints from it INSTANTLY on
  // mount with no fetch gating the first paint (`tests/e2e/specs/work/
  // work-chat-scope.spec.ts`'s E2E-WORK-02/04/20 depend on turn-zero
  // rendering) — never by disabling the query the way this used to work.
  //
  // FRONT-1813 finding 1 (root-caused, not merely suppressed): a DISABLED
  // query is never refetched by `invalidateQueries`'s default
  // `refetchType: 'active'` — TanStack v5 only refetches an observer whose
  // `enabled !== false` (`@tanstack/query-core`'s `Query.isActive`) — so the
  // old `enabled: isChat && !initialScope` meant a chat that started scoped
  // (opened from the Projects board) could NEVER pick up a later
  // `chat-scope-changed` SSE invalidation: the query had no active observer
  // to refetch. That's exactly the shape Eddy's own `work_chat_move` tool
  // needs — it has no client-side mutation hook of its own, so the
  // SSE-triggered invalidation above is the ONLY way an already-open chat's
  // marker learns about a tool-driven move/remove. So the query stays
  // enabled regardless of `initialScope`.
  //
  // A first draft fixed only that and kept the query's result type as a
  // bare `ChatScopeEntity | null`, then tried to suppress the resulting
  // race (an ordinary mount-time refetch landing a 404 before the backend's
  // own fire-and-forget scope persist finished, overwriting the seed) with
  // `refetchOnMount: false`. That papered over ONE trigger of the race — a
  // browser `online` event after a wifi flap/wake/VPN switch still refetches
  // (`refetchOnReconnect` defaults to on and nothing here turns it off) and
  // hits the identical 404-wipes-seed bug. The real fix is in
  // `useChatScope`'s queryFn: it now returns a discriminated
  // `ChatScopeResult` that keeps "not yet known" (a 404) distinct from
  // "known — confirmed, possibly null" (a 200) instead of collapsing both
  // into the same `null`, and `resolveEffectiveScope` prefers the seed for
  // anything that isn't `known: true` — safe by construction against EVERY
  // refetch trigger, so nothing needs suppressing.
  const seededScope: ChatScopeEntity | null = initialScope
    ? { id: initialScope.entityId, name: initialScope.name, type: initialScope.type, archived: initialScope.archived }
    : null;
  const { data: scopeResult } = useChatScope(isChat ? inboxSessionId : undefined);
  // `resolveEffectiveScope` is the ONE precedence rule (`use-chat-scope.ts`,
  // unit-tested there): the query's `known: true` result — including a
  // genuine `entity: null` from a real move/remove — always wins; anything
  // else (still loading, or `known: false` from a 404) falls back to
  // `seededScope`. There is no separate local "override" layer beyond
  // that: the manual picker's mutation (`use-chat-mutations.ts`'s
  // `onSuccess`) writes the SAME cache entry this query reads, so its
  // result already reflects a manual move/remove by the time this
  // component re-renders.
  const effectiveScope: ChatScopeEntity | null = resolveEffectiveScope(seededScope, scopeResult);
  const setChatScope = useSetChatScope();
  function handleSelectScope(target: ContainerPickerOption) {
    if (!isChat) return;
    setChatScope.mutate({ sessionId: inboxSessionId, entityId: target.id });
  }
  function handleClearScope() {
    if (!isChat) return;
    setChatScope.mutate({ sessionId: inboxSessionId, entityId: null });
  }

  // Documents carry their body in a separate store, not in `result`.
  // staleTime 0 (FRONT-221): always re-pull on open — a background "Update
  // document" turn may have rewritten the file since we last cached it, and the
  // global 60s staleTime would otherwise show stale content on reopen.
  const { data: docData, isLoading: docLoading } = useDocumentDetail(
    isDocument ? filename : null,
    { staleTime: 0 },
  );
  // Uploads carry their body in the user_files store: the text itself for
  // text files, the extracted text for PDFs, and — for a `binary` tool-made
  // Word/Excel/RTF file — extracted text too (FRONT-2365, healed on read by
  // GET /api/files/:id if the row predates that extraction); empty only for
  // image uploads and a binary file with no extractor (e.g. .pptx, .zip).
  const { data: fileData, isLoading: fileLoading } = useFileDetail(
    fileAsset?.fileId,
  );
  // FRONT-1453: a session reopened from the activity board/pill rail/Chats
  // page may hand this component a STUB `FileAssetItem` — `sessionToModalAsset`
  // only knows the raw upload id from the session id, so kind/description/
  // hasBytes are placeholders. Prefer the live detail row for every one of
  // those fields the instant it lands, so the stub self-corrects instead of
  // staying wrong (a wrong `binary` guess would render the wrong reader
  // branch below) for the rest of the session. `fileAsset.name`/`.fileId`
  // already have their own live-preference (`fileData?.name ?? …` below) or
  // never change (`fileId`), so they're untouched here.
  const liveFileKind = fileData?.kind ?? fileAsset?.fileKind;
  // FRONT-1453 review fix: `fileData?.description ?? fileAsset?.description`
  // fell through to the stale stub/snapshot whenever the LIVE description was
  // a real `null` (the user cleared it) — `??` treats a live null exactly
  // like "not loaded yet". Once the live query has actually resolved
  // (`fileData !== undefined`), its value — including null — always wins;
  // the stub/snapshot is a fallback ONLY before that first response lands.
  const liveDescription = fileData !== undefined ? fileData.description : (fileAsset?.description ?? null);
  const liveHasBytes = fileData?.hasBytes ?? fileAsset?.hasBytes ?? false;
  // FRONT-2235: the byline's Created/Updated line, for a document or file
  // asset only — same live-over-stub precedence as `title`/`liveFileKind`
  // above. `undefined` for a chat/briefing feed item, which keeps the old
  // single-date byline unchanged (see the render below).
  //
  // FRONT-2235 review F2: routed through `selectLiveItemDates` so neither
  // side can ever read `feedItem.timestamp`/`fileAsset.timestamp` as a
  // stand-in for the real `updatedAt` — that field is the feed's ORDERING
  // value (chat-activity time for a chatted-with asset) and reading it here
  // showed a conversation's last message time as if it were a document/file
  // edit. A `sessionToModalAsset` stub has neither `createdAt` nor
  // `updatedAt` on its snapshot, so both resolve to `undefined` until the
  // live query below lands.
  let liveDates: LiveItemDatesSource = {};
  if (isDocument) {
    liveDates = selectLiveItemDates(docData, feedItem);
  } else if (fileAsset) {
    liveDates = selectLiveItemDates(fileData, fileAsset);
  }
  const liveCreatedAt = liveDates.createdAt;
  const liveUpdatedAt = liveDates.updatedAt;
  // A stub reopened from a chat has no dates until the live query lands (and
  // never, if that fetch fails) — the byline then drops the date AND its
  // separator, rather than reading "From your Files · · <tag>".
  const bylineDates = resolveItemDates(liveCreatedAt, liveUpdatedAt);
  const hasBylineDates = Boolean(bylineDates.created || bylineDates.updated);
  const tag = chatItem
    ? chatItem.agentLabel ?? ''
    : fileAsset
      ? liveFileKind === 'text'
        ? 'file'
        : `${liveFileKind} file`
      : isDocument
        ? feedItem!.kind === 'research-briefing'
          ? 'Research briefing'
          : 'Document'
        : (feedItem!.taskType || feedItem!.category || 'briefing').toLowerCase();
  const bodyText = isChat
    ? ''
    : isDocument
      ? docData?.content ?? ''
      : fileAsset
        ? fileData?.content ?? ''
        : parseResult(feedItem!.result).body || feedItem!.result;
  const assetLoading = isDocument ? docLoading : isFile ? fileLoading : false;

  // The item's displayed name (FRONT-1107). Each candidate below is the
  // most-current source for its own kind; `resolveDisplayTitle` (modal-asset)
  // owns the precedence between them and documents why each one wins.
  const [renamedName, setRenamedName] = useState<string | null>(null);
  const title = resolveDisplayTitle({
    renamedName,
    // The live thread row first, so a rename (typed or AI) reaches the window tab.
    chatTitle: chatItem ? threadRow?.title ?? chatItem.title ?? 'New chat' : null,
    fileName: fileAsset ? fileData?.name ?? fileAsset.name : null,
    documentTitle: isDocument ? docData?.title ?? null : null,
    snapshotTitle: feedItem ? feedItem.title ?? feedItem.jobName : null,
  });
  // FRONT-3146: keep the window bar's label in step with the window's own title.
  const { setTitle: setWindowTitle } = chrome;
  useEffect(() => {
    if (title) setWindowTitle(title);
  }, [title, setWindowTitle]);

  // The header crumb's plain-text name (FRONT-3152): the pinned agent, else
  // the chat's own name, else the fixed word for a non-chat asset.
  let crumbName = 'Chat';
  if (directAgent) crumbName = directAgent.name;
  else if (chatItem) crumbName = chatItem.title ?? 'New chat';

  // The asset content handed to EDDY on every turn. Two channels need it
  // differently (FRONT-577):
  //
  // - VOICE can only receive *text* in its instructions, so it gets the full
  //   body inline (empty for images — a known voice limit; a PDF's own
  //   extracted text, when it has one, IS included here since FRONT-1608).
  //   For uploads, ride the description along too — it may be the only text
  //   an image has.
  // - TEXT CHAT reads the anchored file as a real attachment instead (see
  //   `anchorAttachment` below) — the ONLY way an image reaches the model at
  //   all, and, for a PDF, the only way the NATIVE document (not just its
  //   text layer) reaches the model. So the per-turn note for
  //   a file asset carries just the "you're viewing X" hint + description — NOT
  //   the body, which would double-send text files. Documents and feed items
  //   have no attachment to ride, so they keep the full body in the note.
  //
  // The chat variant has no anchor at all, so both are undefined.
  // FRONT-1251: a cron-generated item leads with the job that produced it, so
  // "which job made this?" / "when does it run again?" / "change this
  // schedule" are answerable without the user explaining their own inbox.
  // Null for everything else (documents, uploads, anchor-less chats).
  // Two flavours: typed chat can reach the cron tools and may offer to change
  // a schedule; the voice channel's tool whitelist has none, so it is told to
  // hand that off rather than claim it did it.
  const chatSourceNote = feedItem
    ? describeFeedItemSource(feedItem, { canManageSchedule: true })
    : null;
  const voiceSourceNote = feedItem
    ? describeFeedItemSource(feedItem, { canManageSchedule: false })
    : null;
  const prefixed = (note: string | null, body: string) => (note ? `${note}\n\n${body}` : body);
  const assetBody = liveDescription
    ? `${title}\n\n${liveDescription}\n\n${bodyText}`
    : `${title}\n\n${bodyText}`;

  const voiceAssetContext = isChat ? undefined : prefixed(voiceSourceNote, assetBody);
  const assetContext = isChat
    ? undefined
    : isFile
      ? liveDescription
        ? `${title}\n\n${liveDescription}`
        : title
      : prefixed(chatSourceNote, assetBody);

  // FRONT-577: the open file rides every turn as a real attachment so the
  // model reads PDFs/images natively (text/spreadsheets get inlined). Only the
  // fileId matters server-side; the contentType is cosmetic. Available from the
  // asset itself, so it's sent from the first turn even before the body loads.
  const anchorAttachment = fileAsset
    ? fileAssetToContextAttachment(fileAsset, fileData?.contentType ?? '')
    : null;

  // FRONT-1739: ONE decision (see editable-asset.ts) — whether the header
  // pencil shows AND what EDDY is told it may rewrite this turn both read
  // this single result, computed once, so they can never disagree about
  // which items are hand-editable. `canEditAsset` below (used by the pencil
  // and `startEdit`) is derived from the same `editableAsset.canEdit`.
  const editableAsset = resolveEditableAsset({
    isDocument,
    documentFilename: filename,
    // FRONT-1739 review finding 1: the SAME value the header actually
    // displays (`title`, resolved above via `resolveDisplayTitle` — folds in
    // a same-session rename) — never a second, independent read of the
    // document's title that could drift from what's on screen.
    documentTitle: isDocument ? title : undefined,
    fileAsset,
    liveFileKind,
    liveFileName: fileAsset ? (fileData?.name ?? fileAsset.name) : undefined,
  });

  const activeArtifactRef = useRef<ActiveArtifactRef | null>(null);
  // FRONT-2669: whether a Canvas panel exists for this chat, read at send time (assigned below with the Canvas state).
  const canvasPanelRef = useRef(false);
  // Save-before-send (canvas-send-gate.ts): the mounted Canvas editor registers its
  // flush here; the dirty ref mirrors the editor's "unsaved draft" signal so a send
  // path that cannot wait for a save reads it synchronously.
  const canvasFlushRef = useRef<CanvasFlush | null>(null);
  const canvasDirtyRef = useRef(false);
  const [canvasSendHint, setCanvasSendHint] = useState(false);
  const {
    messages,
    status,
    stopRun,
    manuallyStopped,
    lastStreamedRunId,
    sendMessage,
    sendInline,
    sendGatedAction,
    reload,
    historyLoading,
    historySeeded,
    retryMessage,
    deleteMessage,
    activeBranchId,
    forkGroups,
    switchBranch,
    mergeBranch,
    mergingBranchId,
    setContextAttachments,
  } = useAgentChat(inboxSessionId, {
    // FRONT-2594: the Canvas item open NOW, read at send time. The Canvas state is
    // computed further down (it needs `messages`), so the getter reads a ref the
    // render assigns below.
    // While a Canvas draft is unsaved the server copy Eddy would read is out of date,
    // so a send that did not pass the save-before-send gate (retry, a card button, a
    // plan decision, the opening message) names no open item at all.
    getActiveArtifact: () => artifactNamedBySend(canvasDirtyRef.current, activeArtifactRef.current),
    getCanvasPanel: () => canvasPanelRef.current,
    assetContext,
    assetEditable: editableAsset.assetEditable,
    scopeEntityId: effectiveScope?.id,
    folderScopeId: initialFolderScope?.folderId,
    directAgentId: directAgent?.id,
  });

  // FRONT-765 (live-test finding, 2026-09-02): `manuallyStopped` overrides
  // this the instant Stop is pressed, regardless of how long the SDK or the
  // polled background_chats row take to actually settle — see turn-busy.ts.
  const isStreaming = isTurnStreaming(status, manuallyStopped);
  useEffect(() => setOwnTurnLiveSince(isStreaming ? Date.now() : null), [isStreaming]);

  // FRONT-518 (chat variant): deliver the launcher's first message exactly once,
  // after history settles, into this freshly-minted session. A new session's
  // history is empty so it sends immediately; the backend mints the thread on
  // this first user message and names it from that message while the reply
  // is still streaming (FRONT-1804). Mirrors the
  // one-shot `initialMessage` drain home.tsx used for legacy deep-links.
  const sentInitialRef = useRef(false);
  useEffect(() => {
    if (!isChat || !initialMessage || historyLoading || sentInitialRef.current) {
      return;
    }
    sentInitialRef.current = true;
    sendMessage({ text: initialMessage }, initialAttachments, initialImage);
  }, [isChat, initialMessage, initialAttachments, initialImage, historyLoading, sendMessage]);

  // Documents in this chat's context (FRONT-357): every file uploaded into
  // this conversation stays in context — re-sent with each turn — until its
  // chip is removed. The list derives from the message history (uploads ride
  // user messages as attachment metadata); removals persist per session in
  // localStorage so a reopened modal doesn't resurrect a removed file.
  const [removedFileIds, setRemovedFileIds] = useState<Set<string>>(() =>
    loadRemovedFileIds(inboxSessionId),
  );
  const contextFiles = deriveContextFiles(messages, removedFileIds);
  // What EDDY actually receives each turn: the in-chat uploads PLUS the open
  // file itself (FRONT-577). The anchor keeps its own fixed "always part of
  // this chat" chip, so it stays OUT of the removable `contextFiles` list but
  // still gets sent. Deduped in case the same file was also uploaded inline.
  const sentAttachments = anchorAttachment
    ? [anchorAttachment, ...contextFiles.filter((f) => f.fileId !== anchorAttachment.fileId)]
    : contextFiles;
  const contextKey = sentAttachments.map((f) => f.fileId).join(',');
  useEffect(() => {
    setContextAttachments(sentAttachments);
    // contextKey stands in for the array — same ids ⇒ same context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey, setContextAttachments]);

  const removeContextFile = (fileId: string) => {
    setRemovedFileIds((prev) => {
      const next = new Set(prev);
      next.add(fileId);
      persistRemovedFileIds(inboxSessionId, next);
      return next;
    });
    toast.success('Removed from the chat context');
  };

  // FRONT-204/FRONT-2195: when this asset's last question is still running on
  // the server (we reopened the modal after closing it mid-answer, so there's
  // no LOCAL stream attached here), the resume hook in useAgentChat
  // re-attaches to the live turn on its own — the in-progress answer, its
  // tool cards, and the Stop button all stay visible without a banner. This
  // flag is now just "true while the server says the turn is running and no
  // local stream is attached to it *this instant*" — read below for the
  // button-action spinner (`backgroundActive`), the composer's busy state,
  // and the doc-refresh settle effect; it does NOT gate whether the answer
  // renders (see `displayMessages` below, which no longer drops anything).
  const { data: bgRun, isLoading: bgRunLoading, isError: bgRunIsError } = useBackgroundChatForSession(inboxSessionId);
  const turnRunningUnattached = bgRun?.status === 'running' && !isStreaming;
  // FRONT-2195: the composer's Stop/Send visual (and its input-disabling
  // "busy" state) must reflect the SERVER's view of whether this turn is
  // running, not only whether a local stream happens to be attached right
  // now — otherwise reopening a chat onto a still-running turn shows Send
  // (and lets the user try to submit straight into it) instead of Stop.
  // `isQueueBusy` is the existing turn-busy.ts helper that already folds in
  // `manuallyStopped` so a deliberate Stop frees the composer immediately
  // regardless of how long the server takes to catch up (FRONT-765) — reused
  // here for `isBusy`/`isStreaming` so all three composer props (Stop/Send,
  // input-disable, and `queueBusy` below) agree on one boolean.
  const composerBusy = isQueueBusy(isStreaming, bgRun?.status, manuallyStopped);
  // FRONT-3223: the chat's open questions are answered inside the reply box
  // (eddy, 2026-10-28), like the Builder's; flag off, on the card as before.
  const questionDock = useChatQuestionDock({ sessionId: inboxSessionId, enabled: v2 && isChat, turnActive: composerBusy, messages });
  const cardDock = useChatCardDock({
    messages,
    enabled: v2 && isChat,
    onSendMessage: (text) => sendMessage({ text }),
  });
  // FRONT-3211: a chat task settled but its result turn has not run yet. A
  // message typed meanwhile waits in the composer's queue, so EDDY's combined
  // answer lands before it. Only the queue gate reads it: Send and Stop do
  // not, and running tasks alone never make the composer busy.
  const { data: chatTasks, isLoading: chatTasksLoading } = useChatTasks(isChat ? inboxSessionId : null);
  // The composer's Stop released the queue from the results pending at that
  // moment (see `pendingResultTaskIds`); a new send clears `manuallyStopped`
  // and with it the release.
  const [stopReleasedTaskIds, setStopReleasedTaskIds] = useState<ReadonlySet<string>>(() => new Set());
  const taskResultPending = isTaskResultPending(chatTasks, manuallyStopped ? stopReleasedTaskIds : undefined);
  const stopTaskAnswer = useStopTaskAnswer(isChat ? inboxSessionId : null);
  // FRONT-3211: a turn the server started here (a task's result turn) shows
  // live: re-read history until the resume pursuit attaches to it.
  useServerTurnPickup(inboxSessionId, isServerTurnUnattached(bgRun, status, lastStreamedRunId()));

  // FRONT-694: when a deep-research run is active for this session, show the
  // informative "deep research is running" banner. Deep research is the agreed
  // background exception (ADR-0032) — it can't run inline, so without this the
  // kick-off is a silent black box. The signal is the session's current
  // research run (live via the `research-changed` event stream); the banner
  // shows only while it's `running`, so it clears on its own when the run
  // finishes. It's dismissable per run id: closing it hides it until a brand-new
  // run starts (a different id re-shows it), so we don't re-nag on every poll.
  const { data: researchRun } = useResearch(inboxSessionId);
  const runningResearchId =
    researchRun?.status === 'running' ? String(researchRun.id) : null;
  // FRONT-694: the SECOND you approve a deep-research plan, show the banner —
  // don't wait for the run row to round-trip back. This optimistic flag bridges
  // the ~1s gap until the real run appears (the approve handler below sets it).
  // It clears the moment the real run is observed (the real state takes over,
  // keeping the banner truthful once the run finishes), or after a short
  // timeout if a run never materialises (a failed launch must not leave a
  // banner stuck on) — see the effect below, and `shouldClearOptimisticResearch`
  // (research-optimistic-gate.ts) for the decision itself.
  // FRONT-703: shared bound for both the optimistic banner flag and the
  // dismiss-before-we-had-an-id flag below — a failed/never-materialising
  // launch must not leave either stuck on indefinitely.
  const OPTIMISTIC_RESEARCH_WINDOW_MS = 12_000;
  const [optimisticResearch, setOptimisticResearch] = useState(false);
  // FIX 3 (adversarial review, FRONT-1601): this flag used to have NO code
  // clearing it — the comment above always claimed a self-clear, but nothing
  // ever set it back to `false`, so the cobalt "In progress" bar stuck on for
  // the rest of the session once a plan was approved, and re-opening it later
  // showed a blank panel (no run behind it any more). Two exits, mirrored
  // from `shouldClearOptimisticResearch`: the real run round-trips back
  // (`runningResearchId` goes non-null — `isResearchBannerVisible` already
  // ORs it in, so there's no visible gap when this flips off), or the
  // optimistic window elapses with no run ever confirmed.
  useEffect(() => {
    if (!optimisticResearch) return;
    if (shouldClearOptimisticResearch({ runningResearchId, windowElapsed: false })) {
      setOptimisticResearch(false);
      return;
    }
    const timer = setTimeout(() => {
      setOptimisticResearch(false);
    }, OPTIMISTIC_RESEARCH_WINDOW_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimisticResearch, runningResearchId]);
  // FRONT-1500: the bar shows for as long as the run does and nothing the user
  // clicks can hide it — see research-banner-logic.ts.
  const showResearchBanner = isResearchBannerVisible({
    runningResearchId,
    optimistic: optimisticResearch,
  });
  // FRONT-1500 (fifth pass): whether the detail panel is open OVER the
  // conversation. Transient view state on purpose — it is a "let me watch this
  // for a moment", not an acknowledgement, so it isn't persisted anywhere
  // durable. It closes itself when the run ends, which is also what stops
  // the panel's polls.
  //
  // FRONT-1578 correction: this boolean is no longer the WHOLE story — it IS
  // now keyed by run id, via `autoOpenedResearchRunIdRef` a few lines below,
  // which remembers which run this panel has already auto-opened for so a
  // manual collapse isn't undone by the next tick for the SAME run.
  const [researchDetailsOpen, setResearchDetailsOpen] = useState(false);

  // FRONT-1601: a run that settles `completed` while the panel was open
  // stays on screen in a FINISHED state instead of vanishing with no
  // signal — see `research-finished-notice.ts` for the four rules (only
  // `completed`; only when the panel was open; never re-appears once
  // dismissed; no run id, no notice). `dismissedFinishedRunIdRef` is a ref,
  // not state — same reasoning as `autoOpenedResearchRunIdRef` just below:
  // it only needs to be read the next time `finishedNoticeVisible` is
  // computed, never to trigger a render of its own, because the
  // `setResearchDetailsOpen(false)` call that actually closes the panel
  // already causes the re-render that hides the notice.
  const dismissedFinishedRunIdRef = useRef<number | null>(null);
  const finishedNoticeVisible = researchFinishedNotice({
    status: researchRun?.status,
    runId: researchRun?.id ?? null,
    panelWasOpenWhenFinished: researchDetailsOpen,
    dismissedRunId: dismissedFinishedRunIdRef.current,
  });
  // Every path that closes the expanded panel (the bar's own collapse
  // click, Escape, sending a message, and the finished notice's own "Show
  // me the briefing" button) runs through this first, so a dismiss can
  // never be undone by a later tick for the SAME run — see rule 3 in
  // `research-finished-notice.ts`. A no-op unless the notice is actually
  // showing, so it's safe to call unconditionally from a close path shared
  // with the still-running case.
  const dismissFinishedNoticeIfShowing = useCallback(() => {
    if (finishedNoticeVisible && researchRun?.id != null) {
      dismissedFinishedRunIdRef.current = researchRun.id;
    }
  }, [finishedNoticeVisible, researchRun?.id]);
  // FRONT-1601: the run whose fields the panel is allowed to read from right
  // now — the live running run, or (only while the finished notice is
  // genuinely up) the just-completed one. `undefined` for a cancelled/failed
  // run, or a completed run whose notice was never shown/has been
  // dismissed — those never reach the panel at all (`show` is false), so
  // this only needs to answer the question for the states that DO render.
  const researchPanelRun =
    researchRun && (researchRun.status === 'running' || finishedNoticeVisible)
      ? researchRun
      : undefined;

  useEffect(() => {
    // FRONT-1601: don't collapse the panel out from under a finished notice
    // that's still up — `showResearchBanner` alone goes false the instant a
    // run stops `running`, which is exactly the moment a just-completed run
    // needs the panel to STAY expanded.
    if (!showResearchBanner && !finishedNoticeVisible) setResearchDetailsOpen(false);
  }, [showResearchBanner, finishedNoticeVisible]);

  // FRONT-1578: the panel now opens ITSELF the moment a run becomes active,
  // and must never re-open after the user folds it back down. The decision
  // (auto-open on a new run id, stay put otherwise) lives in the pure
  // `nextResearchPanelState` reducer (research-panel-open.ts) so it's
  // unit-tested without React; this effect is only the wiring. The "already
  // auto-opened this run" memory is a REF, not state — it only needs to be
  // read the next time this effect runs, never to trigger a render of its
  // own, and a ref can't fight the manual collapse the way re-deriving from
  // `researchDetailsOpen` on every render would.
  const autoOpenedResearchRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    const next = nextResearchPanelState(
      { open: researchDetailsOpen, autoOpenedRunId: autoOpenedResearchRunIdRef.current },
      runningResearchId,
      'run-active',
    );
    autoOpenedResearchRunIdRef.current = next.autoOpenedRunId;
    if (next.open !== researchDetailsOpen) setResearchDetailsOpen(next.open);
    // `researchDetailsOpen` is deliberately excluded from the deps: this
    // effect must react only to the run id becoming a NEW one, never to the
    // panel being toggled by hand (open or collapsed) — including it would
    // re-run the decider on every manual toggle, and while the decider is a
    // no-op once a run's id is already recorded, there's nothing to gain from
    // re-running it and a real risk in getting that reasoning wrong later.
  }, [runningResearchId]);

  /**
   * FRONT-1578: the user actually SENT a message (composer submit) — collapse
   * the research detail panel back to the bar if it's open. Trigger is send,
   * never focus and never a keystroke. Routed through the same pure decider
   * as the auto-open above; never touches the run, the bar, or Stop — those
   * are driven by `showResearchBanner`/`runId`, untouched here.
   */
  const collapseResearchPanelOnSend = useCallback(() => {
    // FRONT-1601: sending a message is one of the three dismiss paths for a
    // finished notice too (button / collapsing / sending) — mark it before
    // the collapse below so it can't come back for this run.
    dismissFinishedNoticeIfShowing();
    setResearchDetailsOpen((current) => {
      const next = nextResearchPanelState(
        { open: current, autoOpenedRunId: autoOpenedResearchRunIdRef.current },
        runningResearchId,
        'message-sent',
      );
      return next.open;
    });
  }, [runningResearchId, dismissFinishedNoticeIfShowing]);

  // FRONT-542: opening the chat on a finished answer counts as reading it — drop
  // that run's status light from green to grey. Covers both reopening a chat
  // that finished in the background and watching a turn complete live in the
  // modal. Fires once per finished run id; a fresh turn re-arms the light.
  const markBgChatRead = useReadBgChats((s) => s.markRead);
  // FRONT-661: opening a finished chat marks it seen server-side (flips the
  // green/red dot to gray, surviving reload and across devices). Fires for both
  // completed and failed runs once the modal is open. The browser-local
  // markBgChatRead stays for the inbox briefing-row light during rollout.
  const markChatSeen = useMarkChatSeen();
  useEffect(() => {
    if (
      (bgRun?.status === 'completed' || bgRun?.status === 'failed') &&
      bgRun.runId
    ) {
      markBgChatRead(bgRun.runId);
      markChatSeen.mutate(inboxSessionId);
    }
    // markChatSeen is a stable mutation handle; excluded to avoid a re-fire loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgRun?.status, bgRun?.runId, inboxSessionId]);

  // FRONT-787: an effect here used to mark the chat seen the moment the
  // research banner appeared. Its only job was to cancel out FRONT-694's amber
  // research pull-back for the person already looking at the chat; research no
  // longer touches the chat's status, so it had nothing left to do. (The
  // completed/failed mark-seen effect just above is untouched and still covers
  // every case that matters.)

  // Moved up from its original spot below (the "Update document" query-
  // invalidation effect) so the FRONT-754 promote-on-close effect just below
  // can also use it.
  const queryClient = useQueryClient();

  // FRONT-754: the chat-status spine is the authoritative "is this session
  // still busy" signal — the background_chats run plus any pending question,
  // approval or plan. Checking the bg-chat row's status alone is not enough:
  // a turn can settle while the chat still waits on the user. Read via the
  // same hook the launcher pills and header indicator use, scoped to just this
  // session.
  //
  // It does NOT fold in research (FRONT-787 removed the last trace of that,
  // and FRONT-754 itself was closed as invalid for proposing it) — which is
  // the intended launchpad behaviour, not a gap: a detached research is its
  // own Inspector card, so a chat whose turn has finished does not get
  // promoted on close just because a research it started is still going.
  // FRONT-2343: the 2s poll-while-active behaviour (and the cross-replica
  // approval reconcile riding on it) is now driven by `ChatMessages` itself
  // — mounted below with the same `status`/`backgroundActive`
  // (`turnRunningUnattached`) inputs this used to compute its own
  // `pollWhile` from. See chat-messages.tsx / turn-active-logic.ts for the
  // one explanation. This call is still needed here for the `chatStatuses`
  // data the spine-state read just below uses — same query key as
  // ChatMessages' own call, so React Query dedupes the two observers into
  // one request rather than polling twice.
  const { data: chatStatuses } = useChatStatuses([inboxSessionId]);
  const chatSpineState = chatStatuses?.[inboxSessionId]?.state;

  // FRONT-433/754: a zen-modal turn only becomes a real "background job"
  // once the user leaves the modal while it's still busy. Record that on
  // unmount (close = × / backdrop / Esc, all of which unmount this
  // conditionally-rendered modal) so the Inspector surfaces a monitored-chat
  // row for it. Either signal is enough to promote: the bg-chat row is still
  // literally `running` (a normal in-flight turn, and the fallback if the
  // spine query hasn't resolved yet), OR the spine says working/needs-you (a
  // chat paused on a question/approval the user walked away from). Turns
  // watched to completion with NEITHER signal firing are never promoted, so
  // quick in-modal Q&A stays out of the panel — and, under the launchpad
  // model, a detached deep research correctly does NOT promote either: the
  // chat is done the moment the research detaches, the research itself is the
  // Inspector's own first-class card. Read via refs so the cleanup sees the
  // value at close time, not mount time.
  const promoteDetached = useDetachedBgChats((s) => s.promote);
  const bgRunRef = useRef(bgRun);
  bgRunRef.current = bgRun;
  const chatSpineStateRef = useRef(chatSpineState);
  chatSpineStateRef.current = chatSpineState;
  useEffect(() => {
    return () => {
      const stillBusy = shouldPromoteOnClose({
        bgRunStatus: bgRunRef.current?.status,
        spineState: chatSpineStateRef.current,
      });
      if (!stillBusy) return;
      promoteDetached(inboxSessionId);
      // FRONT-754: don't wait for the next poll — the row must be there the
      // instant the user opens the Inspector, even on its very first mount
      // this session (when there's nothing yet to make it refetch on its own).
      queryClient.invalidateQueries({ queryKey: backgroundChatKeys.all });
      queryClient.invalidateQueries({ queryKey: chatStatusKeys.all });
    };
  }, [promoteDetached, inboxSessionId, queryClient]);

  // FRONT-2195: while that background turn is still writing, its answer row
  // sits in chat_messages at status="streaming" — which /history flags as
  // `streamStatus: 'interrupted'`. Before this ticket the client could not
  // re-attach the live stream after reopening, so that trailing half-written
  // row was dropped here and replaced with a "working in background" banner.
  // It can re-attach now (useAgentChat's resume hook, gated on the server's
  // own `streamLive` flag) — the in-progress answer, its tool cards (incl. the
  // desktop-task card) and the Stop button must stay visible at all times, so
  // nothing is dropped here anymore. `messages` IS `displayMessages`.
  const displayMessages = messages;
  const hasMessages = displayMessages.length > 0;
  // FRONT-3223 (eddy, 2026-10-28): a chat the person has not written in yet
  // shows no start time, and while the conversation is empty its reply box
  // sits in the middle of the window, sliding down when the first message goes.
  const hasUserMessage = showStartLine(displayMessages);
  const emptyConversation = v2 && isChat && isEmptyConversation(displayMessages, historyLoading);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const { lift: composerLift, animate: composerSlides, placing: composerPlacing } = useCenterLift(chatScrollRef, emptyConversation);
  const expandDrop = useExpandDrop(composerLift);

  // "Update document" (FRONT-221): EDDY replies with the full rewritten document
  // as plain markdown (NOT a tool call — a large doc stuffed into a tool-call
  // JSON arg truncates and the save fails). The actual save now happens
  // SERVER-SIDE at turn finalization (the chat-proxy writes the reply back into
  // the document via the `saveReplyToDocument` flag set in `updateDocument()`),
  // so it completes in the background even if the modal is closed. This effect
  // no longer saves — it only refreshes the reader's queries when an
  // update turn settles while the modal is still open, so the open reading pane
  // shows the new content live. If the user already left, reopening refetches
  // the document fresh anyway. Refs keep the deps minimal so it only acts on a
  // real streaming→idle transition.
  const updatePendingRef = useRef(false);
  const prevStreamingRef = useRef(isStreaming);
  useEffect(() => {
    const justFinished = prevStreamingRef.current && !isStreaming;
    prevStreamingRef.current = isStreaming;
    if (!justFinished || !updatePendingRef.current) return;
    updatePendingRef.current = false;
    if (!isDocument || !filename) return;
    // The server already saved (or skipped) by now; pull the fresh content.
    queryClient.invalidateQueries({ queryKey: documentKeys.detail(filename) });
    queryClient.invalidateQueries({ queryKey: feedKeys.all });
  }, [isStreaming, isDocument, filename, queryClient]);

  // FRONT-221: the case above only fires if we stayed in the modal through the
  // live stream. When the user left mid-run and reopened, there's no local
  // stream attached the instant this effect checks — the turn keeps running
  // as a background run and useAgentChat's resume hook may or may not be
  // attached to it right now. Watch that state settle (turnRunningUnattached
  // true→false) and refresh the reader then, so the freshly-saved document
  // paints the moment the card goes green instead of waiting out the cache.
  // Race-free: the server writes the document before the background-chat row
  // is settled, so "no longer working" implies "saved".
  const prevBgWorkingRef = useRef(turnRunningUnattached);
  useEffect(() => {
    const justSettled = prevBgWorkingRef.current && !turnRunningUnattached;
    prevBgWorkingRef.current = turnRunningUnattached;
    if (!justSettled || !isDocument || !filename) return;
    queryClient.invalidateQueries({ queryKey: documentKeys.detail(filename) });
    queryClient.invalidateQueries({ queryKey: feedKeys.all });
  }, [turnRunningUnattached, isDocument, filename, queryClient]);

  // FRONT-1601: the PRIMARY way this modal now hears about a finished
  // background research run is live — the backend publishes `chat-response`
  // on the user's own channel (`userChannel(userId)`, src/core/event-bus.ts)
  // in addition to the chat's own session channel, and this tab's one
  // EventSource (use-sse.ts) already subscribes to that user channel
  // (FRONT-1113), so the event reaches it carrying its own `sessionId` even
  // when it isn't this tab's canonical session. This effect is the BACKSTOP
  // for when that live delivery doesn't happen — no SSE connection at all
  // (page backgrounded, a dropped/reconnecting stream) — not the primary
  // mechanism any more. `useResearch` above already polls this session's run
  // every 3s while it's `running` (FRONT-695, for exactly this no-subscriber
  // gap); this effect is the one thing that ACTS on the poll noticing the
  // run leave `running`, refreshing this modal's OWN session's history. The
  // decision (and the run-id-keyed "already handled" memory, mirroring
  // `autoOpenedResearchRunIdRef` a few dozen lines up) lives in
  // `shouldRefreshAfterResearch` (research-refresh-gate.ts) so it's
  // unit-tested without React.
  //
  // FIX 3 (adversarial review): `runningResearchSeenIdRef` tracks the run id
  // this modal has actually observed in a `'running'` state — passed as
  // `wasSeenRunning` below. Without it, a chat opened AFTER its research had
  // already finished (mount sees an already-settled run, `lastHandledRunId`
  // still `null`) read identically to a genuine transition and blasted the
  // feed/documents/history caches on every open. Only a run this modal
  // watched go running → settled counts as "just settled."
  const lastHandledResearchRunIdRef = useRef<number | null>(null);
  const runningResearchSeenIdRef = useRef<number | null>(null);
  // FRONT-1601 FIX 4: `messages` for the retry loop below to read WITHOUT
  // being in that effect's own dependency array — depending on `messages`
  // directly would restart (and so indefinitely postpone) the retry
  // schedule on every unrelated message, not just this run's briefing
  // landing.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  useEffect(() => {
    const status = researchRun?.status;
    const runId = researchRun?.id ?? null;
    if (status === 'running' && runId != null) {
      runningResearchSeenIdRef.current = runId;
    }
    const wasSeenRunning = runId != null && runningResearchSeenIdRef.current === runId;
    if (
      !shouldRefreshAfterResearch({
        status,
        runId,
        lastHandledRunId: lastHandledResearchRunIdRef.current,
        wasSeenRunning,
      })
    ) {
      // A settled run seen for the first time (already finished when this
      // chat was opened) still needs to be marked handled here, or the next
      // unrelated re-render would keep re-evaluating it as unhandled.
      if (runId != null && status !== undefined && status !== 'running') {
        lastHandledResearchRunIdRef.current = runId;
      }
      return;
    }

    // FRONT-1601 FIX 4: the run row settles several journal steps BEFORE
    // the briefing is inserted into chat_messages (src/skills/runner.ts's
    // own comment on the gap between the `research-complete` step and the
    // later message-stamp/chat-delivery step) — a SINGLE refetch right on
    // this transition can land inside that gap and see nothing, and the old
    // code stamped the run handled regardless, so it never asked again.
    // Retry on a short bounded schedule instead: keep refetching until the
    // briefing is present in the loaded history, or `RESEARCH_REFRESH_MAX_
    // ATTEMPTS` is spent — `nextResearchRefreshStep` (research-refresh-gate.ts)
    // makes that call each time; only once it says the chase is over do we
    // stamp `lastHandledResearchRunIdRef`, so a still-in-flight briefing
    // can't be abandoned prematurely.
    const chaseRunId = runId!;
    let attempts = 0;
    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const refetchThenEvaluate = () => {
      if (cancelled) return;
      attempts += 1;
      // Same set the SSE `chat-response` handler invalidates for a briefing
      // delivered live (use-sse.ts) — it lands as both a chat message and a
      // saved document, so the conversation and the Files list both need it.
      queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'history', inboxSessionId] });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2392: a live-delivered briefing is a new document — see
      // use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      timerId = setTimeout(() => {
        if (cancelled) return;
        const outcome = nextResearchRefreshStep({
          briefingPresent: historyContainsBriefingFor(messagesRef.current, chaseRunId),
          attempts,
          maxAttempts: RESEARCH_REFRESH_MAX_ATTEMPTS,
        });
        if (outcome === 'retry') {
          refetchThenEvaluate();
        } else {
          // 'briefing-found' or 'gave-up' — either way this run is handled;
          // a 'gave-up' briefing may simply never arrive (a suppressed or
          // failed delivery), and retrying forever would chase nothing.
          lastHandledResearchRunIdRef.current = chaseRunId;
        }
      }, RESEARCH_REFRESH_RETRY_DELAY_MS);
    };

    refetchThenEvaluate();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [researchRun?.status, researchRun?.id, inboxSessionId, queryClient]);

  // FRONT-1601 (FIX 4, adversarial review): "Finish now" on a run that has
  // gathered nothing usable yet is deliberately treated exactly like Stop by
  // the backend — the run settles `cancelled` and no briefing is delivered
  // (deep.ts's `usable.length === 0 && finishRequested` branch, an agreed
  // acceptance criterion, untouched here). The user who just clicked gets no
  // briefing AND no explanation, which reads as broken rather than as
  // "there was nothing to write up." `finishRequestedAt` is the durable
  // marker ONLY a "Finish now" click ever stamps (an ordinary Stop never
  // sets it), so gating on it here keeps a plain Stop's existing silent
  // behaviour untouched. Mirrors the wasSeenRunning gate above so a chat
  // reopened on an already-cancelled, already-finish-requested run doesn't
  // replay this toast on every open — only a genuine running → cancelled
  // transition fires it, once per run.
  const finishCancelledToastRunningSeenRef = useRef<number | null>(null);
  const finishCancelledToastedRunIdRef = useRef<number | null>(null);
  useEffect(() => {
    const status = researchRun?.status;
    const runId = researchRun?.id ?? null;
    if (status === 'running' && runId != null) {
      finishCancelledToastRunningSeenRef.current = runId;
    }
    if (
      status !== 'cancelled' ||
      runId == null ||
      !researchRun?.finishRequestedAt ||
      finishCancelledToastRunningSeenRef.current !== runId ||
      finishCancelledToastedRunIdRef.current === runId
    ) {
      return;
    }
    finishCancelledToastedRunIdRef.current = runId;
    toast.info('Nothing had been found yet, so there was nothing to write up.');
  }, [researchRun?.status, researchRun?.id, researchRun?.finishRequestedAt]);

  // Reading preferences (the "Aa" popover): width sizes the card + column,
  // font size scales the rendered document.
  const [prefs, setPrefs] = useReadingPrefs();
  // FRONT-2594 Canvas: flag + wide-screen query + the per-chat panel state.
  // `historyReady` is the hook's "history loaded AND seeded into the message
  // list" signal (scoped to chat + branch) — NOT `!historyLoading`, which a cached history flips before
  // the messages arrive (that would count the whole history as brand new).
  const canvasFlag = useFeatureFlag(ARTIFACT_WORKSPACE_FLAG, false);
  const canvasWide = useMediaQuery('(min-width: 1024px)');
  const [canvasDirty, setCanvasDirty] = useState(false);
  const onCanvasDirtyChange = useCallback((dirty: boolean) => {
    canvasDirtyRef.current = dirty;
    setCanvasDirty(dirty);
    if (!dirty) setCanvasSendHint(false);
  }, []);
  const canvas = useCanvasState({
    sessionId: inboxSessionId,
    enabled: canvasFlag && isChat,
    messages,
    branchId: activeBranchId,
    historyReady: isChat && historySeeded,
    wide: canvasWide,
    lockActive: canvasDirty,
  });
  canvasPanelRef.current = canvasFlag && isChat;
  // The item the NEXT send tells Eddy about (flag on, panel open, a backend kind).
  activeArtifactRef.current = activeArtifactForSend({
    enabled: canvasFlag && isChat,
    open: canvas.open,
    items: canvas.items,
    activeKey: canvas.activeKey,
  });
  // FRONT-3223: flag off keeps main's capped widths.
  const cardPx = v2 ? READING_CARD_PX : LEGACY_READING_CARD_PX;
  const cardMax = canvasCardMaxWidth(canvas.layout, cardPx[prefs.width], cardPx.full);
  const columnMax = (v2 ? READING_COLUMN_PX : LEGACY_READING_COLUMN_PX)[prefs.width];
  const chatFont = READING_FONT_PX[prefs.fontSize];

  // Inline editing (FRONT-202): the header pencil flips the reader into a
  // text area over the asset's markdown source — and, since FRONT-1107, turns
  // the heading into a name field, so one Save commits both. Documents save
  // through the same PUT the "Update document" flow uses (FRONT-221); text
  // uploads (FRONT-290) save through the Files PUT — both ride the existing
  // mutation hooks so the inbox feed + Files list refresh on their own.
  //
  // Deliberately NOT the shared inline-rename control (`InlineRenameTitle`,
  // used by the Files cards): that one commits on blur, which is wrong here —
  // the blur that matters is the user clicking into the body text area right
  // below, and saving half an edit at that moment is not what they asked for.
  // Here Save commits, Cancel and Esc discard, for the name and body alike.
  const updateDoc = useUpdateDocument();
  const updateFile = useUpdateFile();
  const savePending = updateDoc.isPending || updateFile.isPending;
  // FRONT-1739: was two independent expressions (this gate, and the
  // `assetEditable` request field derived separately) — now both read
  // `editableAsset` (computed once, above, alongside `assetContext`) so they
  // can never drift apart. See editable-asset.ts.
  const canEditAsset = editableAsset.canEdit;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // FRONT-1107: the name is edited in the same session as the body and saved by
  // the same button. (`renamedName`, which holds what this reader shows after a
  // successful save, is declared up by the title derivation that reads it.)
  const [nameDraft, setNameDraft] = useState('');

  const startEdit = () => {
    if (assetLoading || !canEditAsset) return;
    setDraft(bodyText);
    setNameDraft(title);
    setIsEditing(true);
  };

  const cancelEdit = () => setIsEditing(false);

  const saveEdit = () => {
    if (!canSaveEdit({ draft, body: bodyText, nameDraft, name: title, bodyRequired: isDocument }))
      return;
    // Only send a name when the user actually typed a new one. Sending the
    // displayed name on every save would make a body-only edit re-assert
    // whatever name this reader happens to be showing — and for a document
    // that is the parent page's snapshot, with no live source to correct it.
    // A rename made elsewhere in the meantime would be silently undone by
    // someone fixing a typo.
    const nextName = nameDraft.trim();
    const renamed = nextName !== title.trim() ? nextName : null;
    if (fileAsset) {
      if (!fileData) return;
      const fileId = fileAsset.fileId;
      updateFile.mutate(
        {
          id: fileId,
          ...(renamed ? { name: renamed } : {}),
          content: draft,
          description: fileData.description,
        },
        {
          onSuccess: () => {
            // Paint the new content immediately — same reasoning as below.
            queryClient.setQueryData(
              fileKeys.detail(fileId),
              (old: typeof fileData | undefined) => {
                if (!old) return old;
                const updated = { ...old, content: draft };
                // Only when the user actually changed it — see the rename rule
                // above; an untouched name must never be written back.
                if (renamed) updated.name = renamed;
                return updated;
              },
            );
            if (renamed) setRenamedName(renamed);
            setIsEditing(false);
            toast.success('File updated');
          },
          onError: (err) => toast.error((err as Error).message || 'Save failed'),
        },
      );
      return;
    }
    if (!filename) return;
    const docFilename = filename;
    updateDoc.mutate(
      { filename: docFilename, content: draft, ...(renamed ? { title: renamed } : {}) },
      {
        onSuccess: () => {
          if (renamed) setRenamedName(renamed);
          // Paint the new content immediately — the invalidation in the hook
          // refetches right after, but without this the reader would flash the
          // old content for the round-trip. Same for `title` (FRONT-1453: now
          // a live source `title` reads from) — without patching it here too,
          // a rename would flash the OLD heading until the refetch lands.
          queryClient.setQueryData(
            documentKeys.detail(docFilename),
            (old: { filename: string; content: string; title: string } | undefined) => {
              if (!old) return old;
              const updated = { ...old, content: draft };
              if (renamed) updated.title = renamed;
              return updated;
            },
          );
          setIsEditing(false);
          toast.success('Document updated');
        },
        onError: (err) => toast.error((err as Error).message || 'Save failed'),
      },
    );
  };

  // Esc closes — keyboard parity with the × and backdrop click. While editing
  // it only cancels the edit, so a reflex Esc can't throw away a draft; while
  // the deep-research panel covers the chat it peels that off first
  // (FRONT-1500). The precedence lives in `escapeAction`, not here, so it can
  // be pinned by a test.
  //
  // FRONT-1579 fix: this is a `window` keydown listener, so it fires on
  // EVERY Escape, including one meant for something stacked on top of the
  // modal — e.g. the "Chat activity" board's `Popover`. Radix's
  // `DismissableLayer` handles Escape for the innermost open layer in the
  // capture phase and calls `preventDefault()` (never `stopPropagation()`),
  // so without this guard one Escape press both closed the board AND closed
  // the whole chat window. Bubble-phase listeners run after capture, so by
  // the time this fires `defaultPrevented` is already set when a layer above
  // the modal claimed the key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (e.defaultPrevented) return;
      const action = escapeAction(isEditing, researchDetailsOpen, canvasDirty);
      if (action === 'cancel-edit') setIsEditing(false);
      else if (action === 'collapse-research') {
        // FRONT-1601: Escape is "collapsing" too — dismiss the finished
        // notice (if it's up) before it closes, same as every other close
        // path.
        dismissFinishedNoticeIfShowing();
        setResearchDetailsOpen(false);
      } else if (action === 'keep-canvas-draft') {
        // FRONT-2594: a Canvas document with unsaved edits swallows Escape; the
        // window closes only from its × button.
      } else chrome.escape(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, chrome, isEditing, researchDetailsOpen, canvasDirty, dismissFinishedNoticeIfShowing]);

  // FRONT-1991: modal viewers are mutually exclusive, never stacked
  // (CLAUDE.md) — the desktop operator panel is a second full-screen
  // overlay mounted separately in `app-layout.tsx`, so it cannot close
  // THIS one itself the way a surface opening a new overlay from inside an
  // existing dialog normally would. This hook is the other half: the
  // instant a "Watch desktop" click raises a request, this chat window
  // closes itself rather than sitting stacked underneath it.
  useCloseOnOperatorPanelRequest(onClose);
  // FRONT-3146: with `eddy.ui-refactoring` on, the Pro Builder window no
  // longer closes this one — windows coexist in the window layer (at most two
  // side by side, see `lib/stores/window-rules.ts`). Off, FRONT-3003's rule
  // stands for every mount of this overlay (Home and the local ones on
  // Memory, Files, Work…): Pro Builder opening closes it WITHOUT the host's
  // return-to-origin navigation, which would hide Pro Builder again.
  useCloseOnProBuilderOpen(v2 ? noopClose : (onCloseAfterNavigate ?? onClose));

  // Resolve actions ----------------------------------------------------------
  const [pendingWarning, setPendingWarning] = useState<WarningKey | null>(null);

  // FRONT-895 "Create plan from chat" — flag-gated (the backend endpoints
  // fail-closed on the same flag, so this is a visibility gate, not the real
  // enforcement). Fails closed (fallback `false`) like the other recently
  // rolled-out flags (`eddy.graph-health-tab`, `eddy.apps-terminal`).
  const createPlanEnabled = useFeatureFlag('eddy.create-plan-from-chat', false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  // FRONT-1122: the non-chat resolve footer's "Create project" source — null
  // (no button rendered) when the open asset has nothing usable to extract
  // from, e.g. a cron-run feed item with no `runId`.
  const footerPlanSource = !isChat ? derivePlanSourceForFooter({ fileAsset, feedItem }) : null;

  // Direct create-document mutation — used both by "Save as document" (the
  // whole item, FRONT-269) and by "save a single response" below. No LLM.
  const createDoc = useCreateDocument();

  const saveToGraph = () => {
    // FRONT-269: drive the write-to-graph preview gate via a deterministic
    // directive instead of injecting a /write-to-graph slash command for the
    // orchestrator to parse. The `writeToGraph` flag triggers the gate
    // server-side; the friendly chip still shows via `source`. FRONT-230: the
    // preview/approval gate (propose_plan before any write) is unchanged.
    sendGatedAction({
      text: 'Save this to my knowledge graph.',
      writeToGraph: true,
      // FRONT-199: show a friendly status chip instead of this prompt.
      source: { kind: 'button', action: 'save-graph', label: 'Save to Graph' },
    });
  };

  // FRONT-269: "Save as document" (non-document items). The content already
  // exists in the viewed item — there is nothing for the model to decide, so
  // this is a direct create-document call with no LLM round-trip, mirroring
  // `saveResponseAsDocument` below. Lands in Files and the inbox.
  const saveAsDocument = () => {
    const content = bodyText.trim();
    if (!content) {
      toast.error('Nothing to save — this item has no content yet.');
      return;
    }
    createDoc.mutate(
      // FRONT-1737 adversarial-review finding (verified, not fixed — this
      // surface never renders for a live chat): the button that calls this
      // is only rendered inside `{!isChat && (…)}` (this modal's non-chat
      // asset footer — a document/file/feed item), and `isChat` cannot
      // change between that render check and this call, so `isChat` is
      // always `false` here and this ternary can NEVER take its truthy
      // branch — `sessionId` is always `undefined`, and this save path can
      // never auto-attach to a Work container. Left as-is (matching
      // `saveResponseAsDocument`'s shape) rather than hardcoded to
      // `undefined`, since collapsing it would hide the one thing that
      // WOULD make it live again: this button someday also rendering for a
      // chat item.
      { title, content, tags: 'briefing', sessionId: isChat ? inboxSessionId : undefined },
      {
        onSuccess: (doc) => toast.success(resolveDocumentSavedMessage(doc.title, doc.attachedTo)),
        onError: (err) => toast.error((err as Error).message || 'Save failed'),
      },
    );
  };

  // For a document asset: rewrite the original in place, folding in the
  // conversation. EDDY replies with the full document as markdown; the SERVER
  // writes that reply back over the same file at turn finalization (FRONT-221,
  // via `saveReplyToDocument`) so the update lands even if the modal is closed.
  const updateDocument = () => {
    updatePendingRef.current = true;
    // FRONT-269: drive the rewrite via a deterministic directive instead of an
    // injected English instruction. The `updateDocument` flag makes the server
    // own the canonical rewrite prompt and forces the turn inline (FRONT-201) —
    // the model still generates the new document text, but routing is fixed.
    // FRONT-221: saveReplyToDocument tells the server to write the reply back
    // into this file at finalization, so the save is background-safe.
    sendInline({
      text: 'Update this document with our conversation.',
      updateDocument: true,
      // FRONT-199: show a friendly status chip instead of this prompt.
      source: { kind: 'button', action: 'update-document', label: 'Update document' },
      saveReplyToDocument: filename ?? undefined,
    });
  };

  // Per-response: save a single EDDY answer as a brand-new document, instantly
  // (direct create — no agent round-trip). Lands in Files and the inbox —
  // and, when this is a chat scoped to a Work container, also on that
  // container (server-side auto-attach, `src/work/chat-scope-attach.ts`);
  // see `saveAsDocument` above for the same note.
  const saveResponseAsDocument = (text: string) => {
    const docTitle = deriveTitle(text, title);
    createDoc.mutate(
      { title: docTitle, content: text, tags: 'chat-note', sessionId: isChat ? inboxSessionId : undefined },
      {
        onSuccess: (doc) => toast.success(resolveDocumentSavedMessage(doc.title, doc.attachedTo)),
        onError: (err) => toast.error((err as Error).message || 'Save failed'),
      },
    );
  };

  // Resolution bar (FRONT-523, chat variant only) ---------------------------
  // The three one-click actions that turn a transient chat into kept value (or
  // discard it), each reusing an existing wired flow. They act on THIS thread's
  // session (`inboxSessionId`), transition it out of the active pill row, and
  // are user-driven only — EDDY never resolves a chat (epic decision #1).
  const resolveToDocument = useResolveChatToDocument();
  const resolveToGraph = useResolveChatToGraph();
  const trashChat = useTrashChat();
  const restoreChat = useRestoreChat();
  const resolving =
    resolveToDocument.isPending ||
    resolveToGraph.isPending ||
    trashChat.isPending ||
    restoreChat.isPending;

  // Lifecycle state of THIS conversation's thread — fetched for BOTH the chat
  // AND the asset (document/upload) variants (FRONT-1453 review fix). A
  // document/upload conversation reopened via `chatThreadToAsset` (the pill
  // rail, the modal's own chat-activity menu, or the Chats list) can be trashed exactly like an
  // ordinary web chat; this used to be gated on `isChat`, so a binned asset
  // conversation showed no "In Recycle bin" indicator and no Restore action
  // anywhere in the asset footer — the user could type into it with nothing
  // on screen suggesting the conversation was invisible everywhere else.
  const isTrashed = threadRow?.state === 'trashed';

  // Restore a trashed chat back to the reopenable set (→ archived), then close.
  const restoreChatThread = () => {
    restoreChat.mutate(inboxSessionId, {
      onSuccess: () => {
        toast.success('Restored — the chat is back in your open list.');
        onClose();
      },
      onError: (err) =>
        toast.error((err as Error).message || 'Could not restore the chat'),
    });
  };

  // Save as Document: serialize the conversation to markdown (the existing
  // export helper) and POST it; the server saves the document, wires the
  // chat↔document backlink, and stamps resolution=document + keptAt (state/
  // liveness untouched since FRONT-1309 — the chat can still be an active
  // pill). Its document carries a "From chat" badge in Files; we close back
  // to the launcher as the deliberate end of this action either way.
  const resolveChatToDocument = () => {
    if (!hasMessages) {
      toast.error('Nothing to save yet — start the conversation first.');
      return;
    }
    const docTitle =
      chatItem?.title?.trim() || deriveConversationTitle(displayMessages);
    const content = conversationToMarkdown(displayMessages, { title: docTitle }).trim();
    resolveToDocument.mutate(
      { sessionId: inboxSessionId, content, title: docTitle },
      {
        onSuccess: (r) => {
          toast.success(resolveDocumentSavedMessage(r.thread.title ?? docTitle, r.attachedTo));
          onClose();
        },
        onError: (err) => toast.error((err as Error).message || 'Save failed'),
      },
    );
  };

  // Save to Graph: mark the chat resolved-to-graph on the click (the user's
  // declared intent) AND trigger the existing propose/preview gate so EDDY
  // extracts the facts — the plan card appears in this modal for approval, so
  // we deliberately STAY open. Marking on click keeps the action testable now;
  // full graph→chat provenance is the provenance child ticket.
  const resolveChatToGraph = () => {
    if (!hasMessages) {
      toast.error('Nothing to save yet — start the conversation first.');
      return;
    }
    resolveToGraph.mutate(inboxSessionId);
    saveToGraph();
    toast.success('Saving to your graph — approve the preview below to extract the facts.');
  };

  // FRONT-2433: the Chats page's row menu asked for a keep action on open.
  // Runs once, and only once the loaded conversation is on screen: gating on
  // `historyLoading` alone is not enough, because a cached history can settle
  // a render before the messages reach `displayMessages`, and the action
  // would then refuse with "nothing to save yet". The page only offers these
  // actions on a chat that has messages, so waiting for them is safe.
  const ranInitialActionRef = useRef(false);
  useEffect(() => {
    // …and not while a turn is still streaming, or it would save half an answer.
    if (!isChat || !initialAction || historyLoading || !hasMessages || isStreaming || ranInitialActionRef.current) return;
    ranInitialActionRef.current = true;
    if (initialAction === 'save-document') resolveChatToDocument();
    else resolveChatToGraph();
    // The two handlers are re-created every render; the ref makes this fire
    // exactly once, so they are read from the render that first qualifies.
  }, [isChat, initialAction, historyLoading, hasMessages, isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move to Trash: drop the chat into the Recycle bin (reversible, 30-day
  // retention) and close back to the launcher.
  const trashChatThread = () => {
    trashChat.mutate(inboxSessionId, {
      onSuccess: () => {
        showUndoToast({
          noun: 'chat',
          onUndo: () => restoreChat.mutateAsync(inboxSessionId),
        });
        onClose();
      },
      onError: (err) =>
        toast.error((err as Error).message || 'Could not move to the Recycle bin'),
    });
  };

  // FRONT-225: clear the conversation but STAY in the modal. After the server
  // wipes the history, reload() refetches the now-empty history and overwrites
  // the in-memory chat (it exists for exactly this kind of clear action), so the
  // conversation visibly empties while the user keeps working with the asset.
  // (Was: onClose() here, which bounced the user back to the main chat.)
  const clearChat = async () => {
    try {
      await api.delete<{ cleared?: boolean }>(
        `/api/v2/chat/${encodeURIComponent(inboxSessionId)}/history`,
      );
      await reload();
      toast.success('Chat cleared');
    } catch (err) {
      toast.error((err as Error).message || 'Clear failed');
    }
  };

  // FRONT-531: end-of-call handler for the live voice call (restored from the
  // pre-FRONT-518 home surface, now scoped to THIS thread). The shared helper
  // hands the server the transcript held in memory; it writes the recap and
  // the save proposal, both keyed to `inboxSessionId`, then refetches them.
  // FRONT-254: shared with the hands-free call, which outlives this window.
  const handleCallEnded = useCallback(
    (turns: VoiceTurn[]) =>
      persistCallEnded(queryClient, { sessionId: inboxSessionId, branchId: activeBranchId ?? 'main', turns }),
    [inboxSessionId, activeBranchId, queryClient],
  );

  // FRONT-254: the hands-free call. The composer hands its call to the shell
  // (`handsFreeCallProp`), and while ANY hands-free call is up this window
  // tells it what is open, so "archive this" or "what am I looking at"
  // resolves to the chat, document or file on screen.
  const handsFreeCallState = useHandsFreeCall();
  const handsFreeCallProp = useMemo(() => ({ title, onMinimize: onClose }), [title, onClose]);
  const showOpenItem = handsFreeCallState?.showOpenItem;
  const handsFreeCallActive = !!handsFreeCallState?.active;
  const { type: openItemType, id: openItemId } = openScreenItem({
    isChat,
    sessionId: inboxSessionId,
    fileId: fileAsset?.fileId,
    documentFilename: isDocument ? filename : undefined,
  });
  useEffect(() => {
    if (!handsFreeCallActive || !showOpenItem || !openItemType || !openItemId) return;
    return showOpenItem({ type: openItemType, id: openItemId, title });
  }, [handsFreeCallActive, showOpenItem, openItemType, openItemId, title]);

  const runGuarded = (key: WarningKey) => {
    if (isWarningSuppressed(key)) {
      key === 'graph' ? saveToGraph() : clearChat();
      return;
    }
    setPendingWarning(key);
  };

  const resolveWarning = (confirmed: boolean) => {
    const key = pendingWarning;
    setPendingWarning(null);
    if (!confirmed || !key) return;
    key === 'graph' ? saveToGraph() : clearChat();
  };

  // Header action helpers (carried over from the old briefing drawer) --------
  const onShare = async () => {
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title, text: bodyText });
        return;
      }
      await navigator.clipboard.writeText(`${title}\n\n${bodyText}`);
      toast.success('Copied to clipboard');
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      toast.error('Share failed');
    }
  };

  const onDownload = () => {
    const safeName = (title || 'briefing').replace(/[^a-z0-9-_]+/gi, '-');
    if (fileAsset) {
      // Uploads with original bytes (images, PDFs, binaries) download from the
      // raw route; app-created text notes have no bytes row, so those download
      // as a client-side blob of the content (the raw route 404s for them).
      // FRONT-1107: `title`, not the `item` snapshot's name — after a rename in
      // this reader the heading and the downloaded file must agree.
      if (uploadDownloadMode({ hasBytes: liveHasBytes }) === 'raw') {
        const a = document.createElement('a');
        a.href = rawFileUrl(fileAsset.fileId);
        a.download = title;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      downloadBlob(bodyText, title || `${safeName}.md`, 'text/markdown');
      return;
    }
    if (isDocument) {
      downloadBlob(bodyText, filename || `${safeName}.md`, 'text/markdown');
      return;
    }
    const parsed = parseResult(feedItem!.result);
    const text = parsed.kind === 'structured' ? parsed.raw : parsed.body || feedItem!.result;
    downloadBlob(text, `${safeName}.txt`, 'text/plain');
  };

  // FRONT-1453 review fix: a file asset reopened from a bare session id
  // (`sessionToModalAsset`'s stub) always starts with `hasBytes: false` —
  // it has no way to know better until the real detail row lands. Offering
  // Download before that resolves reads `liveHasBytes` as false for a file
  // that actually has original bytes (an image/PDF), so it takes the blob
  // branch above and hands the user an empty `.md` named after the title
  // instead of the real file. `fileData === undefined` covers BOTH "still
  // loading" and "never resolved" (a failed fetch) — Download stays hidden
  // until the real row has actually landed, for every file asset, not just
  // a reconstructed stub.
  const downloadPending = isFile && fileData === undefined;

  // Delete the upload itself (FRONT-290) — parity with the old Files detail
  // page. Only offered for uploads; documents and cron results keep their
  // existing lifecycles. The confirm dialog mounts at the provider level
  // (outside this modal's backdrop subtree), so its clicks can't re-bubble
  // into the backdrop's onClose (and the shared backdrop rule now also ignores
  // portal-re-bubbled clicks: a press that did not start on the backdrop
  // itself never dismisses).
  const deleteFile = useDeleteFile();
  const deleteDoc = useDeleteDocument();
  // FRONT-1019: single-asset restore for the header Trash icon's Undo toast.
  const bulkFiles = useBulkFiles();
  const bulkDocs = useBulkDocuments();
  const confirm = useConfirm();

  // Which asset (if any) the header Trash icon deletes, and how — null hides
  // the icon (cron-run feed items, chat threads, a file asset missing a
  // fileId, or — FRONT-1453 review finding #4 — an asset reopened from a
  // chat-thread row, see `reopenedFromThread` just below). Computed once here
  // so the handlers below mutate using the exact identifier this descriptor
  // carries — the same value the unit tests exercise — rather than
  // re-deriving it separately from fileAsset/filename.
  const deleteDescriptor = deleteDescriptorFor(item);
  // FRONT-1453 review finding #4: a document/upload conversation reopened
  // from a chat-thread row (pill rail, the modal's chat-activity menu, Chats list) must not
  // expose a MORE destructive action than the chat-level trash the caller
  // already offers for that same row — "Delete everything" below deletes the
  // underlying asset too, same reasoning as the header icon above.
  const reopenedFromThread = isReopenedFromThread(item);

  // FRONT-1453: a document/upload conversation reopened from a chat surface
  // (the top-bar activity board, the pill rail, the modal's own chat-activity menu, the Chats
  // list) restores the asset's own reading pane above the conversation —
  // same as opening the identical item from the Inbox row. But the two
  // differ in ONE respect that matters here: the Inbox row's document/file
  // query is effectively always warm by the time it's clicked (the row was
  // already rendered from the same data), while a chat-surface reopen mints
  // a brand-new query cold. `chat-messages.tsx`'s own "snap to bottom on
  // first paint" fires the instant chat HISTORY settles — which, on a cold
  // asset query, routinely happens BEFORE the asset body has finished
  // loading. That scroll then targets a short, still-loading reading pane;
  // once the real (long) body arrives and the pane grows, nothing re-scrolls
  // — measured directly (FRONT-1453 STEP 1): the conversation can end up
  // thousands of pixels below the fold with no further layout change ever
  // bringing it back. That is what "the chat has no old content" looks like
  // to the user, even though every message is sitting right there in the DOM.
  //
  // Fires ONCE per mount, only for a reopened-from-thread asset (the Inbox
  // row's own open path is untouched — its query is already warm in
  // practice, and re-scrolling on every asset refetch there would be a
  // regression, not a fix), and only once BOTH the asset has finished
  // loading and the conversation has messages to show — so it runs after
  // the reading pane has reached its final height, undoing exactly the
  // stranding the early scroll caused.
  const conversationRef = useRef<HTMLDivElement>(null);
  const reopenScrollDoneRef = useRef(false);
  useEffect(() => {
    if (reopenScrollDoneRef.current) return;
    if (!reopenedFromThread || assetLoading || !hasMessages) return;
    reopenScrollDoneRef.current = true;
    // The window's own scroller only: `scrollIntoView` also scrolls the page behind the window.
    scrollConversationToEnd(conversationRef.current);
  }, [reopenedFromThread, assetLoading, hasMessages]);

  const onDeleteFile = async () => {
    if (deleteDescriptor?.kind !== 'file') return;
    const ok = await confirm({
      title: `Delete "${title}"?`,
      description:
        'This moves the file to the Recycle bin. You can restore it within 30 days.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    const fileId = deleteDescriptor.identifier;
    deleteFile.mutate(fileId, {
      onSuccess: () => {
        showUndoToast({
          noun: 'file',
          onUndo: () => bulkFiles.mutateAsync({ action: 'restore', ids: [fileId] }),
        });
        onClose();
      },
    });
  };

  // Delete the document itself (FRONT-861) — same single-item recycle-bin
  // flow as onDeleteFile above, just routed to the document store. The
  // combined "Delete everything" footer button (onDeleteEverything below)
  // already covered documents; this is the standalone header action that was
  // missing for documents specifically (uploads already had it).
  const onDeleteDocument = async () => {
    if (deleteDescriptor?.kind !== 'document') return;
    const ok = await confirm({
      title: `Delete "${title}"?`,
      description:
        'This moves the document to the Recycle bin. You can restore it within 30 days.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    const filename = deleteDescriptor.identifier;
    deleteDoc.mutate(filename, {
      onSuccess: () => {
        showUndoToast({
          noun: 'document',
          onUndo: () => bulkDocs.mutateAsync({ action: 'restore', filenames: [filename] }),
        });
        onClose();
      },
    });
  };

  const onDeleteAsset = () => {
    if (deleteDescriptor?.kind === 'document') onDeleteDocument();
    else if (deleteDescriptor?.kind === 'file') onDeleteFile();
  };

  // FRONT-533 "Delete everything": send the asset (document or file) to the
  // Recycle bin AND clear its attached conversation in one action. The asset
  // trash is the must-succeed step (recoverable for 30 days); clearing the chat
  // is best-effort cleanup. Only offered for documents and uploaded files —
  // cron-run feed items have no persistent asset to trash.
  const deletingEverything = deleteDoc.isPending || deleteFile.isPending;
  const onDeleteEverything = async () => {
    if (!isDocument && !isFile) return;
    const ok = await confirm({
      title: `Delete "${title}"?`,
      description:
        'This moves it to the Recycle bin and clears its chat. You can restore it from the Recycle bin within 30 days.',
      confirmText: 'Delete everything',
      destructive: true,
    });
    if (!ok) return;
    const result = await deleteEverything({
      trashAsset: () =>
        isDocument
          ? deleteDoc.mutateAsync(filename!)
          : deleteFile.mutateAsync(fileAsset!.fileId),
      clearChat: () =>
        api.delete(`/api/v2/chat/${encodeURIComponent(inboxSessionId)}/history`),
    });
    if (!result.ok) {
      toast.error(result.error.message || 'Delete failed');
      return;
    }
    toast.success(
      result.chatCleared
        ? 'Moved to the Recycle bin and cleared its chat — restore it within 30 days.'
        : 'Moved to the Recycle bin — restore it within 30 days.',
    );
    onClose();
  };

  const onAddToCalendar = () => {
    if (!feedItem) return;
    const start = new Date(item.timestamp);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    const description = (bodyText || feedItem.result).replace(/\r?\n/g, '\\n');
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EDDY//Briefing//EN',
      'BEGIN:VEVENT',
      `UID:${item.id || crypto.randomUUID()}@eddy`,
      `DTSTAMP:${toIcsDate(new Date())}`,
      `DTSTART:${toIcsDate(start)}`,
      `DTEND:${toIcsDate(end)}`,
      `SUMMARY:${title.replace(/\n/g, ' ')}`,
      `DESCRIPTION:${description}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const safeName = (title || 'briefing').replace(/[^a-z0-9-_]+/gi, '-');
    downloadBlob(ics, `${safeName}.ics`, 'text/calendar');
  };

  const when = formatDateTime(item.timestamp, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const time = formatTime(item.timestamp);

  // Backdrop dismiss needs the press AND release on the backdrop (see
  // use-backdrop-dismiss), and, like Escape, never throws away a Canvas draft
  // (FRONT-2594): the window then closes only from its × button.
  const backdropDismiss = useBackdropDismiss(() => {
    if (backdropMayClose(canvasDirty)) onClose();
  });
  // FRONT-3146: a docked window has no backdrop to click and is not modal.
  const frameProps = chrome.docked ? {} : { 'aria-modal': true as const, ...backdropDismiss };

  return (
    // The warning dialogs are SIBLINGS of the backdrop, not children: a
    // backdrop click closes the modal (onClose), and React re-bubbles portal
    // events through the React tree — so a warning dialog nested inside the
    // backdrop would fire onClose on every click inside it (FRONT-201), closing
    // the modal mid-save. Keeping them outside the backdrop's subtree avoids that
    // (the shared backdrop rule now also ignores portal-re-bubbled clicks: a
    // press that did not start on the backdrop itself never dismisses).
    <CanvasContext.Provider value={canvas.context}>
    <div
      role="dialog"
      aria-label={title}
      data-docked={chrome.docked ? 'true' : undefined}
      {...frameProps}
      // `items-stretch`, not `items-start`: the panel below sets only a
      // maxHeight, so under `items-start` it shrink-wrapped its content and a
      // short conversation (a fresh chat, a two-line document) opened as a
      // small card pinned 56px from the top with dead space beneath it — "a
      // fraction of the possible height, and not in the center". Stretching
      // makes the panel fill the padded box exactly, which both fills the
      // screen and centres it, and keeps the window a fixed size as messages
      // arrive instead of growing under the reader.
      // FRONT-3146: docked, it fills its slot in the window layer (the layer
      // already carries the shell seam as padding); standalone it is the
      // full-screen overlay.
      className={chrome.docked ? 'absolute inset-0 flex items-stretch justify-center' : 'fixed inset-0 z-50 flex items-stretch justify-center'}
      // FRONT-3142: the app shell's own seam (`--frame-gap`, app.css), so the
      // window has the same left/right/top/bottom margin as the framed shell.
      // Flag off: the old 56px / 24px overlay margins.
      style={{ padding: chrome.docked ? 0 : v2 ? 'var(--frame-gap)' : '56px 24px' }}
    >
      {/* FRONT-2694: the blur is a SIBLING layer behind the panel, never the
          panel's ancestor. When the browser drops a backdrop-filter layer for
          a frame (seen with a deck scrolling in the canvas iframe), it drops
          that layer's whole subtree — with the blur on the parent, the entire
          chat window vanished for a frame. Now a dropout costs at most a frame
          of unblurred background. zIndex -1 keeps it under every child of this
          stacking context. pointer-events none (FRONT-2751): the backdrop
          dismiss rule closes only when press and release land on the backdrop
          element itself, so a scrim that caught clicks kept the window open. */}
      {!chrome.docked && (
      <div
        aria-hidden
        data-testid="chat-window-scrim"
        className="absolute inset-0"
        style={{
          zIndex: -1,
          pointerEvents: 'none',
          background: 'rgba(20, 20, 26, 0.42)',
          backdropFilter: 'blur(10px) saturate(0.9)',
          WebkitBackdropFilter: 'blur(10px) saturate(0.9)',
          animation: 'eddy-chat-bg-in 220ms ease-out',
        }}
      />
      )}
      <style>{`
        @keyframes eddy-chat-bg-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes eddy-chat-panel-in {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        /* Font-size pref scales the rendered document. The reader column sets
           its own font-size; the markdown body + headings ride it in em so
           the whole document grows together. Chrome (eyebrow/title/byline)
           uses fixed tokens and is unaffected. */
        .eddy-reading .markdown-body { font-size: 1em; line-height: 1.65; }
        .eddy-reading .markdown-body h1 { font-size: 1.5em; }
        .eddy-reading .markdown-body h2 { font-size: 1.28em; }
        .eddy-reading .markdown-body h3,
        .eddy-reading .markdown-body h4 { font-size: 1.12em; }
        /* FRONT-530: the chat conversation must honor the font-size pref too.
           Each bubble carries a fixed small text size, which re-bases the
           markdown body inside it to that fixed size and severs it from the
           reading column's font-size — so in a chat the slider moved nothing.
           Inside the conversation we re-root the bubble text to the column's
           em (proportional line-height so large text doesn't crowd); the
           document/file reader body sits outside this block and is unchanged. */
        .eddy-reading .eddy-chat-convo .text-sm { font-size: 1em; line-height: 1.55; }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        // The measurable handle for the window's own frame. `role="dialog"`
        // sits on the full-screen backdrop above, so measuring the dialog
        // measures the whole viewport and any assertion about the window's
        // height or centring silently reads 0/720 instead (E2E-CHAT-11 hit
        // exactly that on its first run).
        data-testid="chat-window-panel"
        // FRONT-2522: a direct agent chat wears the primary accent as a
        // frame (theme token, so light and dark both follow) — it must be
        // obvious on open that Eddy's routing is not in play.
        data-direct-agent={directAgent ? 'true' : undefined}
        className={`flex flex-col w-full overflow-hidden bg-card text-foreground border ${
          directAgent ? 'border-primary ring-2 ring-primary/40' : 'border-border'
        }${canvasCardClass(canvas.layout) ? ` ${canvasCardClass(canvas.layout)}` : ''}`}
        style={{
          // FRONT-3146: a docked window takes the whole window layer; the reading
          // width still limits the message column inside (`columnMax`).
          maxWidth: chrome.docked ? undefined : cardMax,
          // `100%` of the backdrop's padded box, NOT a hand-computed
          // `calc(100vh - 112px)`: the two have to agree with the padding
          // above or the panel silently over/undershoots the frame the next
          // time that padding is touched. The stretch does the sizing; this
          // is the belt.
          maxHeight: '100%',
          borderRadius: v2 ? 'var(--frame-radius)' : 'var(--radius-xl-ds)',
          boxShadow: 'var(--shadow-lg)',
          animation: 'eddy-chat-panel-in 240ms ease-out',
        }}
      >
        {/* Eyebrow header — matches Builder's top-left crumb (FRONT-2379):
            same bar height/padding and the same wordmark + "/" + bold-title
            shape, so the logo sits at the identical offset from the panel's
            top-left corner in both windows. */}
        {/* FRONT-2484: tighter, and the two purely informational pills (the
            "started" badge and the Fast/Thoughtful indicator) step out below
            `sm` — at phone width they pushed the Close button off the edge,
            leaving no way out of the window on a touch screen. */}
        <div className="flex items-center gap-2 sm:gap-3 h-[60px] px-3 sm:px-4 border-b border-border shrink-0">
          {/* FRONT-2379 fix-round: unlike Builder's crumb (build-window.tsx,
              which sits alone in its bar and stays `shrink-0` end to end), this
              crumb shares its left group with the "started" badge and the chat
              activity menu — so on a narrow window it must give way to them
              instead of painting over the badge. `overflow-hidden` plus letting
              "Chat" and the tag suffix shrink+truncate (only the wordmark and
              the "/" stay fixed-width) keeps the crumb's right edge from ever
              crossing into the badge. */}
          {/* FRONT-3223: `eddy.ui-refactoring` on — the editable title with the
              suggested name (FRONT-3138); off — main's fixed-width crumb with
              the rename-in-place name (FRONT-3152). */}
          {/* Off branch, FRONT-3152: the crumb holds a chat's name now, so it is a FIXED
              width — a long or short name never moves the "started" badge and
              the chat switcher that follow it, so switching chats keeps them
              in place. It still shrinks (basis = width) when the bar runs out
              of room, and the name truncates inside it. */}
          {v2 ? (
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden" data-testid="chat-window-crumb">
            {v2 ? (
              <EddyMark size={20} className="shrink-0 text-foreground" />
            ) : (
              <EddyWordmark size={15} className="shrink-0 text-foreground" />
            )}
            <span aria-hidden className="shrink-0 text-[length:var(--text-md)] text-muted-foreground/50">
              /
            </span>
            {v2 && isChat && !directAgent ? (
              // eddy, 2026-10-26: the chat's own name, editable in place, with
              // a wand for an AI-suggested short title. The live thread row
              // wins over the snapshot the window was opened with.
              <ChatWindowTitle sessionId={threadRow ? inboxSessionId : null} title={title} />
            ) : (
              <span className="min-w-0 truncate text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground">
                {directAgent ? directAgent.name : 'Chat'}
              </span>
            )}
            {tag && (
              <span className="min-w-0 truncate text-[length:var(--text-sm)] text-muted-foreground">
                · {tag}
              </span>
            )}
          </div>
          ) : (
          <div className="group flex w-[22.5rem] items-center gap-1.5 min-w-0 overflow-hidden" data-testid="chat-window-crumb">
            {/* FRONT-3152: at phone width the wordmark and "/" step out so the
                chat's name gets the room — they left it a few pixels. */}
            {/* A wrapper, since the wordmark's own inline display beats a class. */}
            <span className="shrink-0 max-sm:hidden">
              <EddyWordmark size={15} className="text-foreground" />
            </span>
            <span aria-hidden className="shrink-0 text-[length:var(--text-md)] text-muted-foreground/50 max-sm:hidden">
              /
            </span>
            {/* FRONT-3152: a chat's crumb is its own name, renamed in place
                (same control as the Chats page). Renaming needs the thread
                row, which is minted on the first message — before that the
                rename would 404, so the name is plain text until it exists.
                A direct agent test keeps the agent's name and is not
                renamable here. */}
            {isChat && !directAgent && threadRow ? (
              <EditableChatTitle
                sessionId={inboxSessionId}
                title={threadRow.title ?? chatItem.title ?? null}
                clickToEdit
                editInPopover
                className="min-w-0"
                textClassName="text-[length:var(--text-md)] font-semibold tracking-[-0.2px]"
                triggerTestId="chat-window-rename"
                inputTestId="chat-window-rename-input"
              />
            ) : (
              <span
                data-testid="chat-window-name"
                className="min-w-0 truncate text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground"
              >
                {crumbName}
              </span>
            )}
            {tag && (
              <span className="min-w-0 truncate text-[length:var(--text-sm)] text-muted-foreground">
                · {tag}
              </span>
            )}
          </div>
          )}
          {directAgent && (
            <Tip label="Messages go straight to this agent — EDDY does not route or add tools">
              <Badge variant="default" data-testid="direct-agent-badge" className="shrink-0">
                Direct test
              </Badge>
            </Tip>
          )}
          {/* Flag off: the start time is a badge in the header, as before. */}
          {!v2 && (
            <span
              data-testid="chat-window-started-badge"
              className="max-sm:hidden inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 font-mono text-[length:var(--text-xs)] text-muted-foreground whitespace-nowrap"
            >
              <span className="size-1.5 rounded-full bg-success" />
              started {time}
            </span>
          )}
          {/* FRONT-1579: replaces the old full-width SWITCH pill strip below
              the header with the same "Chat activity" dropdown the app top
              bar shows — one control for hopping between open threads,
              wherever it's found. Row click still swaps the modal onto that
              thread in place (`onSwitchThread`, unchanged).

              Placement is deliberate (Mattias, 2026-08-24): this sits in the
              LEFT group next to the "Chat" crumb, ahead of the flex spacer
              (the start time that used to sit beside it is the first line of
              the message list now, FRONT-3146) — it describes
              WHICH conversation you are in, so they belong with its name,
              not among the act-on-it controls (Fast/Thoughtful, edit,
              delete, close) on the right. The crumb carries `min-w-0` so its
              tag suffix ellipsizes instead of shoving this group rightwards
              when a chat's tag is long. */}
          {/* eddy, 2026-10-26: no "Chat activity" dropdown in the window — the
              top bar lists every chat, question and window. Just this chat's
              own lamp, like the Build window's, while it works or waits. */}
          {/* FRONT-3223: with `eddy.ui-refactoring` off the top bar does not list the
              chats, so the dropdown stays in the window. */}
          {isChat &&
            (v2 ? (
              <ChatLamp sessionId={inboxSessionId ?? null} />
            ) : (
              <ChatActivityMenu openSessionId={inboxSessionId} onSwitch={onSwitchThread} onNewChat={onNewChat} />
            ))}
          <div data-window-actions-after className="flex-1" />
          <CanvasHeaderControls
            layout={canvas.layout}
            count={canvas.items.length}
            unseen={canvas.unseen}
            onSetOpen={canvas.setOpen}
          />
          {/* FRONT-820: a persistent, always-visible indicator of the active
              Fast/Thoughtful mode for this conversation — unlike the composer
              pill, this is visible no matter how far the thread is scrolled. */}
          {isChat && !modelPickerOn && headerModelMode && (
            <Tip label={`This conversation is set to your ${headerModelMode} model`}>
              <span
                data-testid="chat-header-model-mode"
                className="max-sm:hidden inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[length:var(--text-xs)] font-medium text-primary whitespace-nowrap"
              >
                {headerModelMode === 'fast' ? (
                  <Lightning size={11} weight="fill" />
                ) : (
                  <Brain size={11} weight="fill" />
                )}
                {headerModelMode === 'fast' ? 'Fast' : 'Thoughtful'}
              </span>
            </Tip>
          )}
          <HeaderIcon icon={CalendarPlus} title="Add to calendar" onClick={onAddToCalendar} hide={isDocument || isFile || isChat} />
          <HeaderIcon
            icon={PencilSimple}
            title={isFile ? 'Edit file' : 'Edit document'}
            onClick={startEdit}
            hide={!canEditAsset || isEditing}
          />
          <HeaderIcon
            icon={Trash}
            title={deleteDescriptor?.label ?? 'Delete'}
            onClick={onDeleteAsset}
            hide={!deleteDescriptor}
            destructive
          />
          <HeaderIcon icon={Share} title="Share" onClick={onShare} hide={isChat} />
          <HeaderIcon icon={DownloadSimple} title="Download" onClick={onDownload} hide={isChat || downloadPending} />
          {/* The role is per-session instructions for EDDY's orchestrator — a
              pinned agent never reads it, so the control would do nothing. */}
          {isChat && !directAgent && (
            <ChatRoleButton
              hasRole={!!chatRoleInstructions}
              errored={chatRoleErrored}
              onClick={() => setRoleDialogOpen(true)}
            />
          )}
          {/* No "New chat" in the window header: "+ Chat" in the top bar starts
              one (eddy, 2026-10-26). `onNewChat` stays for the callers that
              still pass it; nothing in the header calls it any more. */}
          {/* FRONT-3148: no "Aa" here — reading width and font size are set in
              User settings → Reading and the sidebar user menu. */}
          {/* FRONT-3223: both header controls return with `eddy.ui-refactoring` off. */}
          {!v2 && (
            <>
              <HeaderIcon
                hide={!isChat || !onNewChat}
                icon={Plus}
                title={LEGACY_NEW_CHAT_LABEL}
                onClick={() => onNewChat?.()}
              />
              <LegacyReadingPrefsButton prefs={prefs} onChange={setPrefs} />
            </>
          )}
          {/* While a Canvas draft has unsaved edits Escape is deliberately ignored
              (`escapeAction`), so the hint must not promise it. */}
          {/* FRONT-3164: this conversation in its own browser window. */}
          <WindowPopOutButton kind="chat" id={inboxSessionId} title={title} />
          <WindowMinimizeButton />
          <Tip label={canvasDirty ? 'Close' : 'Close (Esc)'}>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="size-8 inline-flex items-center justify-center rounded-[10px] text-foreground hover:bg-muted transition-colors"
            >
              <X size={18} />
            </button>
          </Tip>
        </div>
        {/* FRONT-2594: below `sm` the narrow Chat | Canvas switch lives in its own
            full-width row directly under the header (nothing else null-renders
            here), so the header keeps every control it has without Canvas. */}
        <CanvasHeaderControls
          placement="row"
          layout={canvas.layout}
          count={canvas.items.length}
          unseen={canvas.unseen}
          onSetOpen={canvas.setOpen}
        />

        {/* FRONT-2195: the "still working" banner (FRONT-663) that used to
            sit here is retired — reopening a chat whose run is still going
            now re-attaches to the live answer directly (streaming text, tool
            cards, Stop button) instead of hiding it behind a placeholder. */}

        {/* FRONT-1500: the conversation area, as one positioned box. The
            deep-research bar is a shrink-0 band at the top of it and the
            scrolling column fills the rest — same layout as before. What the
            `relative` buys is the expanded state: the research detail panel is
            `absolute inset-0`, so it covers exactly this box (the whole chat)
            and nothing else — the modal's own header and the composer below
            stay reachable. Without a positioned parent that panel would escape
            to the backdrop and cover the entire viewport. */}
        <div className="relative flex flex-1 min-h-0 flex-col">
        {/* FRONT-694: the deep-research surface. Distinct from the working
            banner above — one cobalt bar (FRONT-1578: was teal) for as long
            as the run lasts, which opens the full detail panel (stage, work
            log, sources, Stop) over the conversation on click.
            FRONT-1601: `show` additionally stays true while the finished
            notice is up (`researchFinishedNotice` above) — the bar/panel
            must not vanish the instant a WATCHED run stops `running`, only
            once the notice has actually been dismissed. `researchPanelRun`
            is the run whose fields the panel may read RIGHT NOW: either the
            live running run, or (only while the finished notice is
            genuinely showing) the just-completed one — never a settled run
            outside that window, which is what keeps a cancelled/failed run
            from ever leaking its stale phase/stage into a panel that
            shouldn't be visible at all. */}
        <ChatResearchBanner
          show={showResearchBanner || finishedNoticeVisible}
          expanded={researchDetailsOpen}
          onToggle={() => {
            dismissFinishedNoticeIfShowing();
            setResearchDetailsOpen((open) => !open);
          }}
          phase={researchPanelRun?.phase}
          runId={researchPanelRun?.id}
          stage={researchPanelRun?.stage}
          startedAt={researchPanelRun?.startedAt}
          completedAt={researchPanelRun?.completedAt}
          deep={researchPanelRun?.deep}
          finishRequestedAt={
            researchRun?.status === 'running' ? researchRun.finishRequestedAt : undefined
          }
          // FIX 7: `finishedNoticeVisible` is a plain `boolean`, not the
          // literal `true` the discriminated `finished: true` branch needs
          // — spread a per-branch object literal instead of passing
          // `finished`/`onShowBriefing` as separate always-present
          // attributes, so the two stay tied together and TypeScript can
          // actually enforce "onShowBriefing exists whenever finished is
          // true" at this call site rather than merely documenting it.
          {...(finishedNoticeVisible
            ? {
                finished: true as const,
                onShowBriefing: () => {
                  dismissFinishedNoticeIfShowing();
                  setResearchDetailsOpen(false);
                },
              }
            : { finished: false as const })}
        />

        {/* Scrolling reading column: the asset, then the conversation. */}
        {/* The test id lets E2E-CHAT-11 assert its own precondition: that the
            content inside this region is SHORTER than the region itself. Only
            then does "the window fills the screen" prove the fill — with
            content tall enough to fill the frame on its own, the check would
            pass just as happily on the shrink-wrapping version it exists to
            catch. */}
        {/* The scrollbar's room is kept from the start, on both sides so the column
            stays centred: when the history loads and starts to scroll, nothing
            jumps sideways (eddy, 2026-10-26). */}
        <div
          ref={chatScrollRef}
          data-testid="chat-window-scroll" className="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable_both-edges]"
          // Flag off: no reserved gutter (FRONT-3223).
          style={v2 ? undefined : { scrollbarGutter: 'auto' }}
        >
          <div
            data-testid="chat-window-column"
            className="eddy-reading mx-auto"
            // Side padding = the reply box form's `px-5`, so the messages and
            // the box share one edge (eddy, 2026-10-28). Flag off: the old
            // 32px side padding (FRONT-3223).
            style={{ maxWidth: columnMax, ...(v2 ? { padding: '44px 1.25rem 28px' } : { padding: LEGACY_COLUMN_PADDING }), fontSize: chatFont }}
          >
            {/* Asset reader (document/file/cron-run only). The chat variant
                (FRONT-518) has no anchor — it opens straight into the
                conversation below. */}
            {!isChat && (
            <>
            {/* Eyebrow */}
            <div className="flex items-center gap-2.5 mb-4 uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
              <span className="h-px w-5.5 bg-primary" style={{ width: 22 }} />
              <span className="text-primary font-semibold">{isFile ? 'File' : 'Inbox item'}</span>
              <span className="text-muted-foreground/60">·</span>
              <span>{tag}</span>
            </div>

            {/* Title. FRONT-1107: in edit mode this is a text field, sitting
                exactly where the heading was so the reader doesn't reflow —
                the name and the body are one edit, committed by one Save. */}
            {isEditing ? (
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Enter commits the whole edit (name + body), matching the
                  // Save button — an input with no form would otherwise swallow it.
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  saveEdit();
                }}
                disabled={savePending}
                maxLength={200}
                aria-label="Name"
                placeholder="Name"
                className="w-full rounded-md border border-primary/40 bg-background px-2 py-1 font-semibold text-foreground outline-none focus:border-primary"
                style={{ fontSize: 'var(--text-3xl)', lineHeight: 1.15, letterSpacing: '-0.012em', margin: '0 0 14px' }}
              />
            ) : (
              <h1
                className="font-semibold text-foreground"
                style={{ fontSize: 'var(--text-3xl)', lineHeight: 1.15, letterSpacing: '-0.012em', margin: '0 0 14px' }}
              >
                {title}
              </h1>
            )}

            {/* Byline strip */}
            <div className="flex items-center gap-2.5 flex-wrap text-[length:var(--text-base)] text-muted-foreground border-b border-border" style={{ paddingBottom: 26, marginBottom: 26 }}>
              <span className="size-[26px] rounded-lg bg-foreground text-background inline-flex items-center justify-center font-semibold text-[length:var(--text-2xs)]">E</span>
              <span>
                {isFile ? (
                  <>From your <strong className="text-foreground font-semibold">Files</strong></>
                ) : (
                  <>Surfaced by <strong className="text-foreground font-semibold">EDDY</strong></>
                )}
              </span>
              {/* FRONT-2235 review F6: a research briefing is a saved
                  `memory_documents` row (`kind: 'research-briefing'`), so
                  `isDocument` is true for it too — it gets the Created/
                  Updated pair here deliberately, not a bug. */}
              {isDocument || isFile ? (
                hasBylineDates && (
                  <>
                    <span className="text-muted-foreground/60">·</span>
                    <ItemDates
                      createdAt={liveCreatedAt}
                      updatedAt={liveUpdatedAt}
                      className="font-mono text-[length:var(--text-sm)]"
                    />
                  </>
                )
              ) : (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="font-mono text-[length:var(--text-sm)]">{when}</span>
                </>
              )}
              <span className="text-muted-foreground/60">·</span>
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[length:var(--text-xs)] font-medium text-primary">
                <Lightning size={10} weight="fill" /> {tag}
              </span>
            </div>

            {/* FRONT-2550: an upload's or document's own tags, each removable
                (saves at once). Only from the LIVE detail row — the stub
                asset carries no tags — and it renders nothing when there are
                none. `-mt-[10px] mb-[26px]` mirrors the In-context row's own
                margins below so the byline→tags→context rhythm stays 16px. */}
            {isDocument && docData && (
              <ItemTags
                key={docData.filename}
                className="-mt-[10px] mb-[26px]"
                source="document"
                itemRef={docData.filename}
                tags={docData.okfTags ?? []}
                taggedByEddyAt={docData.taggedByEddyAt}
                trashed={!!docData.trashedAt}
              />
            )}
            {isFile && fileData && (
              <ItemTags
                key={fileData.id}
                className="-mt-[10px] mb-[26px]"
                source="file"
                itemRef={fileData.id}
                tags={fileData.okfTags ?? []}
                taggedByEddyAt={fileData.taggedByEddyAt}
                trashed={!!fileData.trashedAt}
              />
            )}

            {/* In-context chips (FRONT-357): what EDDY sees on every turn of
                this conversation — the open asset itself (fixed) plus every
                file uploaded into the chat, each removable via its ×. */}
            <div
              className="flex items-center gap-2 flex-wrap"
              style={{ margin: '-10px 0 26px' }}
            >
              <span className="uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
                In context
              </span>
              <Tip label={`${title} — the open ${isFile ? 'file' : 'item'} is always part of this chat`}>
                <span
                  className="inline-flex items-center gap-1.5 rounded-md border border-primary/25 bg-primary/10 px-2 py-1 text-xs text-primary max-w-[260px]"
                >
                  <FileText size={12} className="shrink-0" />
                  <span className="truncate">{title}</span>
                </span>
              </Tip>
              {contextFiles.map((f) => (
                <Tip key={f.fileId} label={`${f.name} — uploaded into this chat`}>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs max-w-[260px]"
                  >
                    <Paperclip size={12} className="shrink-0" />
                    <span className="truncate">{f.name}</span>
                    <Tip label="Remove from context — EDDY stops using this file in future answers">
                      <button
                        type="button"
                        className="opacity-60 hover:opacity-100 shrink-0"
                        onClick={() => removeContextFile(f.fileId)}
                        aria-label={`Remove ${f.name} from context`}
                      >
                        <X size={12} />
                      </button>
                    </Tip>
                  </span>
                </Tip>
              ))}
            </div>

            {/* Asset reader — or, while editing (FRONT-202), the asset's
                markdown source in a text area with Save/Cancel. Uploads
                (FRONT-290) render per kind: images inline, PDFs as a link plus
                their extracted text, binaries as a download link — or, when a
                healed tool-made Word/Excel/RTF file's detail carries text
                (FRONT-2365, `binaryAssetHasBody`), that link plus the text
                too. */}
            {assetLoading ? (
              <div className="flex items-center gap-2 text-[length:var(--text-base)] text-muted-foreground">
                <CircleNotch size={14} className="animate-spin" /> Loading…
              </div>
            ) : isEditing ? (
              <div>
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={savePending}
                  autoFocus
                  aria-label="Document content"
                  className="font-mono min-h-[280px] leading-relaxed"
                  style={{ fontSize: '0.85em' }}
                />
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    className="gap-1.5"
                    onClick={saveEdit}
                    disabled={
                      !canSaveEdit({
                        draft,
                        body: bodyText,
                        nameDraft,
                        name: title,
                        bodyRequired: isDocument,
                      }) || savePending
                    }
                  >
                    {savePending ? (
                      <CircleNotch size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} weight="bold" />
                    )}
                    Save changes
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEdit}
                    disabled={savePending}
                  >
                    Cancel
                  </Button>
                  <span className="text-[length:var(--text-xs)] text-muted-foreground">
                    Esc cancels
                  </span>
                </div>
              </div>
            ) : liveFileKind === 'image' ? (
              <img
                src={rawFileUrl(fileAsset!.fileId)}
                alt={title}
                className="max-w-full rounded-lg border border-border"
              />
            ) : liveFileKind === 'pdf' ? (
              <div>
                <a
                  href={rawFileUrl(fileAsset!.fileId)}
                  target="_blank"
                  rel="noreferrer"
                  className="mb-4 inline-flex items-center gap-2 text-[length:var(--text-sm)] underline"
                >
                  Open PDF
                </a>
                <MarkdownRenderer content={bodyText || '_No extracted text._'} />
              </div>
            ) : liveFileKind === 'binary' ? (
              binaryAssetHasBody(bodyText) ? (
                <div>
                  <a
                    href={rawFileUrl(fileAsset!.fileId)}
                    target="_blank"
                    rel="noreferrer"
                    className="mb-4 inline-flex items-center gap-2 text-[length:var(--text-sm)] underline"
                  >
                    Download {title}
                  </a>
                  <MarkdownRenderer content={bodyText} />
                </div>
              ) : (
                <a
                  href={rawFileUrl(fileAsset!.fileId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[length:var(--text-sm)] underline"
                >
                  Download {title}
                </a>
              )
            ) : (
              <MarkdownRenderer content={bodyText} />
            )}
            </>
            )}

            {/* FRONT-2195: no separate "still working" indicator here — the
                in-progress answer itself (streamed live or re-attached on
                reopen) is the indicator; see the removed-banner note above. */}

            {/* Conversation. For an asset it appears once the user starts
                chatting; for a chat (FRONT-518) it IS the body, so it always
                renders (no asset divider/eyebrow above it). */}
            {/* FRONT-1453: `data-testid` is the stable anchor both the
                reopen-scroll effect above and E2E-CHAT-12's own geometry
                assertion measure against — never a firstElementChild walk. */}
            {(isChat || hasMessages || historyLoading) && (
              <div
                ref={conversationRef}
                data-testid="chat-conversation"
                className={`eddy-chat-convo${isChat ? '' : ' mt-7 pt-6 border-t border-border'}`}
              >
                {/* The start time is the first line of the conversation, scrolling
                    with it — not a pill in the title bar (FRONT-3146). Flag off:
                    it is the header badge instead (FRONT-3223). */}
                {v2 && hasUserMessage && (
                  <p
                    data-testid="chat-window-started"
                    className="mb-4 text-center font-mono text-[length:var(--text-xs)] text-muted-foreground"
                  >
                    started {time}
                  </p>
                )}
                {!isChat && (
                  <div className="flex items-center gap-2.5 mb-5 uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
                    <span className="bg-primary" style={{ height: 1, width: 22 }} />
                    <span className="text-primary font-semibold">Conversation</span>
                  </div>
                )}
                {historyLoading && !hasMessages ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-48 ml-auto" />
                    <Skeleton className="h-16 w-3/4" />
                  </div>
                ) : (
                  <ChatQuestionsDockedContext.Provider value={questionDock.enabled}>
                  <ChatMessages
                    messages={displayMessages}
                    status={status}
                    sessionId={inboxSessionId}
                    activeBranchId={activeBranchId}
                    onRetry={retryMessage}
                    onDelete={deleteMessage}
                    onSendMessage={(text, options) => sendMessage({ text, ...options })}
                    forkGroups={forkGroups}
                    onSwitchBranch={switchBranch}
                    onMergeBranch={mergeBranch}
                    mergingBranchId={mergingBranchId}
                    onSaveAsDocument={saveResponseAsDocument}
                    backgroundActive={turnRunningUnattached}
                  />
                  </ChatQuestionsDockedContext.Provider>
                )}
              </div>
            )}
          </div>
        </div>
        </div>{/* /conversation area */}

        {/* Reply bar — matched to the reading column width, text-only. */}
        {/* No rule above the composer, in either window (eddy, 2026-10-26; the old window too since 2026-10-30). */}
        <div
          data-testid="chat-composer-area"
          data-centered={composerLift ? 'true' : undefined}
          className={`shrink-0 ${liftTransition(composerSlides, composerPlacing)}`}
          style={liftStyle(expandDrop.held ? 0 : composerLift)}
        >
          <div className="mx-auto" style={{ maxWidth: columnMax }}>
            {/* FRONT-230: the interactive plan/preview card lives HERE, bound to
                the modal's own inbox session — so a /write-to-graph preview is
                approvable in the modal and (via usePlan's refetchOnMount) comes
                back when the modal is reopened. onDecision mirrors home.tsx but
                routes every synthetic turn through the modal's sendMessage so
                the write runs in THIS session. Renders nothing unless a pending
                plan exists for the session. */}
            <div className="pt-3">
              <PlanCard
                sessionId={inboxSessionId}
                // FRONT-1412 review finding: missing here, so the FRONT-196
                // guard (no plan decision while the assistant turn is still
                // streaming) was inert on the main desktop chat — the modal
                // already computes this exact streaming state above.
                turnActive={isStreaming}
                onDecision={(d, opts) => {
                  // Approved plan handed to the background engine (ADR-0027):
                  // don't send the inline cue — the run-card carries it.
                  if (d === 'approve' && opts?.background) {
                    // FRONT-694: a research plan just went to the background
                    // engine — show the "deep research is running" banner the
                    // instant it's approved (optimistic), and nudge an immediate
                    // refetch so the real run takes over within a beat.
                    if (opts.kind === 'research') {
                      setOptimisticResearch(true);
                      queryClient.invalidateQueries({
                        queryKey: researchKeys.bySession(inboxSessionId),
                      });
                    }
                    return;
                  }
                  if (d === 'comment') {
                    sendMessage({
                      text: `Here are my comments on this plan before I approve it:\n\n${opts?.comment ?? ''}\n\nPlease discard the current draft and propose an updated plan that incorporates this feedback. Don't start any work yet — wait for my approval on the new plan.`,
                    });
                    return;
                  }
                  sendMessage({
                    text:
                      d === 'approve'
                        ? 'Plan card decision recorded via the UI button — begin executing the plan now (no further approval tool call needed).'
                        : 'Plan rejected. Acknowledge and stand by.',
                  });
                }}
              />
              {/* FRONT-568: a live voice call now ends with a recap + save
                  proposal on every surface (chat, file, inbox feed item), so the
                  save card is actionable on all of them — like the plan card
                  above. Renders nothing unless a pending proposal exists for
                  this session. */}
              <SaveProposalCard
                sessionId={inboxSessionId}
                // FRONT-1148: propose_saves doesn't end the model's turn the
                // way propose_plan does, so a redraft comment sent while the
                // turn is still streaming would race it — same FRONT-196
                // reasoning as the PlanCard above.
                turnActive={isStreaming}
                onDecision={(d, opts) => {
                  if (d === 'comment') {
                    sendMessage({ text: saveProposalCommentMessage(opts?.comment ?? '') });
                    return;
                  }
                  sendMessage({
                    text:
                      d === 'approve'
                        ? saveProposalApprovedMessage(opts?.editedItems)
                        : SAVE_PROPOSAL_DECLINED_MESSAGE,
                  });
                }}
              />
            </div>
            {/* FRONT-1191: the "About: <name>" marker, directly above the
                composer, for a chat scoped to a Work container. FRONT-1738:
                also a control — the move/remove icon button beside the
                marker, gated on `hasMessages` (a chat with no messages has
                no session row to write a scope onto yet — see
                `ChatScopeChip`'s own docblock). FRONT-1813: `ChatScopeChip`
                renders nothing for an UNFILED chat (`effectiveScope` null) —
                filing now happens via the project's "Project chat" button
                (`ask-eddy-button.tsx`, renamed from "Ask Eddy" by FRONT-1808)
                or Eddy's own `work_chat_move` tool, never this control — so
                the wrapper is gated on `effectiveScope` here
                too, or an unfiled chat would show an empty padded block
                above the composer on every ordinary conversation.
                FRONT-1813 adversarial-review finding 8: `data-testid` on the
                wrapper itself (not just the chip inside it) so E2E-WORK-22
                actually guards this gate — the chip already renders `null`
                for an unfiled chat regardless of the wrapper's own gate, so
                asserting only the chip's testid/text stays green even if
                this `effectiveScope &&` check is reverted and the empty
                padded block comes back. */}
            {/* FRONT-3211: the same row carries one chip per chat task (slow
                agent work that left the turn); `ComposerChipRow` renders
                nothing when there is neither a scope nor a task, so an
                unfiled chat without tasks still has no padded block, and
                `chat-scope-wrapper` stays the filed chat's row only. */}
            {isChat && (
              <ComposerChipRow
                sessionId={inboxSessionId}
                rowTestId={effectiveScope ? 'chat-scope-wrapper' : 'chat-task-row'}
                leading={
                  effectiveScope ? (
                    <ChatScopeChip
                      scope={effectiveScope}
                      hasMessages={hasMessages}
                      onOpen={(id) => {
                        navigate(`/work/${id}`);
                        (onCloseAfterNavigate ?? onClose)();
                      }}
                      onSelect={handleSelectScope}
                      onClear={handleClearScope}
                    />
                  ) : undefined
                }
              />
            )}
            {/* FRONT-776: the folder equivalent of the "About: <name>" Work
                marker above — only ever known on the mint (`initialFolderScope`,
                see its own docblock for the no-reopen-fetch caveat).
                FRONT-1738 finding 2: hidden once `effectiveScope` is set —
                filing this chat under a project clears `scope_folder_id`
                server-side (`setSessionScope`), so a folder chip still
                showing here would claim a folder scope that no longer
                exists. */}
            {isChat && initialFolderScope && !effectiveScope && (
              <div className="px-3 pb-2">
                <Tip label="Open this folder">
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/files/folder/${encodeURIComponent(initialFolderScope.folderId)}`);
                      (onCloseAfterNavigate ?? onClose)();
                    }}
                    className="inline-flex max-w-[420px] shrink-0 items-center gap-1.5 self-start rounded-md border border-border bg-secondary px-2.5 py-1.5 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
                  >
                    <Folder size={13} className="shrink-0" />
                    <span className="min-w-0 truncate">Folder: {initialFolderScope.name || 'Untitled'}</span>
                  </button>
                </Tip>
              </div>
            )}
            {canvasSendHint && (
              <p data-testid="canvas-send-hint" role="status" className="px-5 pt-2 text-xs text-muted-foreground">
                {CANVAS_SEND_HOLD_HINT}
              </p>
            )}
            <ChatInput
              onExpandChange={expandDrop.onExpandChange}
              desktopComposer={isChat}
              // FRONT-357: attachments now flow through (the composer's
              // paperclip was always visible here, but the previous handler
              // silently dropped them). They ride this message AND join the
              // in-context chips above for all subsequent turns.
              onSubmit={(text, attachments, image, meta) => {
                const deliver = () => {
                  // FRONT-1578: sending is the trigger, not focus or a
                  // keystroke — collapse the research detail panel (if open)
                  // the moment the message actually goes out.
                  collapseResearchPanelOnSend();
                  // FRONT-2555: a queued send never answers an approval card.
                  if (meta?.desktopSignal?.aborted) throw new Error('desktop-request-cancelled');
                  // FRONT-3223: an open question batch answered in the box goes
                  // out as its answers, the typed text as the note. A message
                  // with attachments stays a message.
                  const answered = !attachments?.length && !image ? questionDock.answer(text) : null;
                  if (answered) return answered.then((message) => sendMessage({ text: message }, [], undefined, meta?.desktopSignal));
                  return sendMessage({ text, ...(meta?.queued ? { queuedSend: true } : {}) }, attachments, image, meta?.desktopSignal);
                };
                // FRONT-2594: no Canvas editor mounted (flag off, closed, Preview, a
                // deck or sketch on show) -> exactly the synchronous send of before.
                const flush = canvasFlag && isChat ? canvasFlushRef.current : null;
                if (!flush) {
                  return deliver();
                }
                // An open Canvas draft is saved first (the editor's own version-checked
                // save). Held back -> the rejection makes the composer put the typed
                // message (and attachments) back; the editor shows its own notice.
                return gateSendOnCanvasDraft(flush).then((gate) => {
                  if (!gate.send) {
                    setCanvasSendHint(true);
                    throw new Error('canvas-draft-unsaved');
                  }
                  setCanvasSendHint(false);
                  // The save landed: the server copy is current, so this turn may name it.
                  canvasDirtyRef.current = false;
                  return deliver();
                });
              }}
              // FRONT-765: also cancel the server-side turn (not just the local
              // SSE read) — the fallback covers the brief window before the
              // live SSE-delivered runId (see use-agent-chat.ts) has arrived.
              // Gated to a row this poll itself has seen as still running
              // (PR-panel finding — the poll's own cache can momentarily lag
              // the previous turn right after a new one starts).
              // FRONT-3211: while a task's result is on its way, the reply
              // being stopped may be its result turn, which the server
              // started: stop it through the server's queue as well (the
              // route stops only a RUNNING result turn, never one waiting
              // behind the person's own reply), and release the queued
              // draft at once, as any stopped reply does.
              onStop={() => {
                if (isChat && taskResultPending) {
                  setStopReleasedTaskIds(new Set(pendingResultTaskIds(chatTasks)));
                  stopTaskAnswer.mutate();
                }
                stopRun(runningFallbackRunId(bgRun));
              }}
              // FRONT-2555: a typed yes/no mid-turn answers the waiting card.
              onAnswerWhileBusy={isChat ? (text) => answerCardByText(inboxSessionId, text) : undefined}
              // FRONT-2195: was `isStreaming` (local-only) — see `composerBusy`
              // above. `isBusy` disables composer controls and gates Enter-to-
              // send; `isStreaming`, when passed, directly decides whether the
              // Send button swaps to Stop (ChatInput's `showStop = isStreaming
              // ?? isBusy`). Passing the same server-aware boolean to both
              // means Stop shows, and works (`onStop` above already cancels
              // the server run), whenever the server reports the turn running
              // — reopened-and-unattached included — not only while a local
              // stream happens to be attached. `queueBusy` below already used
              // this exact boolean, so typing a new message while the turn is
              // busy still queues exactly as before (unchanged).
              isBusy={composerBusy}
              isStreaming={composerBusy}
              // FRONT-761: an errored turn must hold a queued draft, never
              // auto-send into the failure. FRONT-1163: also true from the
              // server-recorded bgRun status, not just the local SDK's own
              // status — a queued draft restored after a remount has no local
              // status to read (chat.status resets fresh on every mount), so
              // the bgRun outcome is the only truth available for it.
              turnError={status === 'error' || bgRun?.status === 'failed' || bgRun?.status === 'cancelled'}
              // FRONT-761: the queue fires only when the turn has genuinely
              // settled — the local stream can drop mid-turn while the server
              // keeps writing (FRONT-204/FRONT-638), and firing into that
              // window splices the queued bubble into the running turn.
              // FRONT-765: `manuallyStopped` overrides the bgRun poll here
              // too (turn-busy.ts) — a deliberately-stopped round must free
              // the queue immediately (decideQueuedSend's `stopRequested`
              // check then holds the draft rather than firing it), not stay
              // "busy" against a row that won't flip out of `running` until
              // the abandoned round finishes on its own. Same value as
              // `isBusy`/`isStreaming` above (`composerBusy`) — one boolean,
              // three props, so Stop/Send, input-disable and the queue gate
              // can never disagree about whether the turn is busy.
              queueBusy={composerBusy || taskResultPending}
              // FRONT-1163: the bgRun poll's first fetch hasn't answered yet —
              // hold off resolving a restored queued draft until it has, or a
              // still-in-flight turn could get misread as already settled.
              // A settled 401 collapses `isLoading` to false exactly like a
              // clean answer (pr-panel finding) — fold in `isError` too so
              // "we gave up trying to know" never reads as "we know it's
              // fine"; it just leaves the restored draft queued instead.
              // FRONT-3211: the same for the chat's tasks: until the list has
              // answered, a restored draft cannot know a result is on its way
              // and would fire ahead of it.
              queuePending={bgRunLoading || bgRunIsError || chatTasksLoading}
              activeBranchId={activeBranchId}
              // FRONT-568: the live voice call is available on every surface
              // (chat, file, inbox feed item) — bound to THIS session so turns +
              // recap + save card land here. Auto-start only when the launcher
              // opened us via the phone button. The binding rule lives in the
              // unit-pinned `voiceCallBinding` helper.
              voiceSessionId={voiceBinding.voiceSessionId}
              // FRONT-570: seed a voice call launched here with the open
              // file/document's content, so the voice agent already knows the
              // asset (the chat history is loaded server-side from the session).
              voiceAssetContext={voiceAssetContext}
              // FRONT-1336: a call started from a Work-scoped chat inherits the
              // container. Sent from here because the scope only reaches the DB
              // on the first typed turn, and this button is often the first thing
              // pressed.
              voiceScopeEntityId={effectiveScope?.id}
              // FRONT-1841: a file dropped/pasted/picked in this composer is
              // auto-filed onto the same container, the fifth and last of
              // this ticket's five auto-file surfaces.
              uploadScopeEntityId={effectiveScope?.id}
              // FRONT-2522: a direct agent chat is text-only — the live call
              // and every upload path would route around the pinned agent.
              hideLiveCall={voiceBinding.hideLiveCall || directAgent !== null}
              hideAttach={directAgent !== null}
              hideImageGen={directAgent !== null}
              // Fast/Thoughtful picks Eddy's orchestrator model and the slash
              // menu lists Eddy's commands — neither reaches a pinned agent, so
              // neither is offered. The hook also stops sending `modelMode`.
              hideModelSwitch={directAgent !== null}
              hideSlashMenu={directAgent !== null}
              placeholder={questionDock.placeholder ?? (directAgent ? `Message ${directAgent.name}…` : undefined)}
              topSlot={questionDock.topSlot ?? cardDock.topSlot}
              sendGate={questionDock.sendGate}
              autoStartCall={voiceBinding.autoStartCall}
              onCallEnded={handleCallEnded}
              // FRONT-254: with the hands-free flag on, the call started here
              // outlives this window — Minimize (or simply closing) leaves it
              // running in the strip at the top. Ignored with the flag off.
              handsFreeCall={directAgent === null ? handsFreeCallProp : undefined}
              // FRONT-820: scope the Fast/Thoughtful mode to this conversation.
              sessionId={inboxSessionId}
            />
          </div>
        </div>

        {/* Resolution bar (chat variant, FRONT-523): turn a transient chat into
            kept value — Save as Document / Save to Graph — or discard it to the
            Recycle bin. One red (Trash) per the color rule; the keep actions
            reuse the established secondary+primary-hover footer style.
            FRONT-1437: removed the decorative "Resolve chat" caption — it read
            as a dead control since it was plain text, not a button. The
            trashed-state "In Recycle bin" indicator stays. */}
        {isChat && (
        <div
          data-testid="chat-resolution-bar" className="flex flex-wrap items-center justify-center gap-2 px-[22px] py-3.5 min-h-[64px] shrink-0"
          // Flag off: the old bar keeps its compact height; like the reworked one it has no rule above,
          // no fill and its actions are centred.
          style={v2 ? undefined : LEGACY_RESOLUTION_BAR_STYLE}
        >
          {isTrashed && (
            <span className="uppercase text-[length:var(--text-xs)] font-semibold tracking-[0.08em] text-muted-foreground whitespace-nowrap">
              In Recycle bin
            </span>
          )}
          {isTrashed && (
            <Badge variant="destructive-soft" className="gap-1">
              <Trash size={12} weight="regular" /> Recycled
            </Badge>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
            onClick={resolveChatToDocument}
            disabled={!hasMessages || resolving}
          >
            {resolveToDocument.isPending ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <FileText size={14} weight="regular" />
            )}{' '}
            Save as document
          </Button>
          {/* FRONT-895: turns this conversation into a project + tasks
              without resolving/closing the chat — sits at the Save-to-Graph
              slot, ahead of it, flag-gated (the backend fails closed on the
              same flag regardless). Labelled "Create project" since FRONT-1141
              — sentence case, matching the Work page's own "New project". */}
          {createPlanEnabled && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
              onClick={() => setCreatePlanOpen(true)}
              disabled={!hasMessages || resolving}
            >
              <ClipboardText size={14} weight="regular" /> Create project
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="gap-2 hover:bg-primary hover:text-primary-foreground"
            onClick={resolveChatToGraph}
            disabled={!hasMessages || resolving}
          >
            <Brain size={14} weight="regular" /> Save to Graph
          </Button>
          {/* A trashed chat is already in the bin — offer Restore instead of a
              no-op "Move to Recycle bin". */}
          {isTrashed ? (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
              onClick={restoreChatThread}
              disabled={resolving}
            >
              {restoreChat.isPending ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <ArrowUUpLeft size={14} weight="regular" />
              )}{' '}
              Restore
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-destructive hover:text-white"
              onClick={trashChatThread}
              disabled={resolving}
            >
              {trashChat.isPending ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <Trash size={14} weight="regular" />
              )}{' '}
              Move to Recycle bin
            </Button>
          )}
        </div>
        )}

        {/* Resolve footer (asset variants only). FRONT-1437: removed the
            decorative "Close chat" caption — it read as a dead control since
            it was plain text, not a button. FRONT-1453 review finding #1: the
            "In Recycle bin" badge + Restore action, previously chat-variant
            only, now also appear here — a document/upload conversation
            reopened from a thread row can be trashed exactly like a plain
            chat, and without this the user typed into an invisible,
            purge-clocked conversation with no on-screen sign anything was
            wrong. */}
        {!isChat && (
        <div className="flex flex-wrap items-center gap-2 px-[22px] py-3.5 border-t border-border bg-muted shrink-0">
          {isTrashed && (
            <span className="uppercase text-[length:var(--text-xs)] font-semibold tracking-[0.08em] text-muted-foreground whitespace-nowrap">
              In Recycle bin
            </span>
          )}
          {isTrashed && (
            <Badge variant="destructive-soft" className="gap-1">
              <Trash size={12} weight="regular" /> Recycled
            </Badge>
          )}
          {isTrashed && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
              onClick={restoreChatThread}
              disabled={resolving}
            >
              {restoreChat.isPending ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <ArrowUUpLeft size={14} weight="regular" />
              )}{' '}
              Restore
            </Button>
          )}
          <div className="flex-1" />
          {/* FRONT-1122: same "Create project" entry the chat footer offers,
              pointed at this document/file/briefing as its source. Hidden
              entirely (not just disabled) when no usable source can be
              derived, e.g. a cron-run item with no runId. */}
          {createPlanEnabled && footerPlanSource && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
              onClick={() => setCreatePlanOpen(true)}
            >
              <ClipboardText size={14} weight="regular" /> Create project
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            className="gap-2 hover:bg-primary hover:text-primary-foreground"
            onClick={() => runGuarded('graph')}
          >
            <Brain size={14} weight="regular" /> Save to Graph
          </Button>
          {isDocument ? (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
              onClick={updateDocument}
            >
              <FileText size={14} weight="regular" /> Update document
            </Button>
          ) : !fileAsset ||
            uploadHasTextBody(liveFileKind ?? fileAsset.fileKind) ||
            ((liveFileKind ?? fileAsset.fileKind) === 'binary' && binaryAssetHasBody(bodyText)) ? (
            // Uploads only get "Save as document" when they have a text body
            // (text notes, PDFs with extracted text) — there is nothing to
            // copy into a document for images. A binary Word/Excel/RTF file
            // healed on read (FRONT-2365 round 4, `binaryAssetHasBody`) gets
            // it too once its detail carries extracted text, same as the
            // asset-reader branch above renders that text instead of a bare
            // download link.
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-primary hover:text-primary-foreground"
              onClick={saveAsDocument}
            >
              <FileText size={14} weight="regular" /> Save as document
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5 hover:bg-destructive hover:text-white"
            onClick={() => runGuarded('delete')}
          >
            <Trash size={14} weight="regular" /> Clear Chat
          </Button>
          {/* FRONT-533: remove the document/file together with its chat, routed
              through the Recycle bin (recoverable for 30 days). Not shown for
              cron-run feed items, which have no persistent asset to delete —
              nor (FRONT-1453 review finding #4) for a conversation reopened
              from a chat-thread row, same reasoning as the header Trash icon
              (`deleteDescriptor`) just above: this button is EVEN MORE
              destructive (it clears the chat too), so it gets the identical
              guard. */}
          {(isDocument || isFile) && !reopenedFromThread && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1.5 hover:bg-destructive hover:text-white"
              onClick={onDeleteEverything}
              disabled={deletingEverything}
            >
              {deletingEverything ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <Trash size={14} weight="regular" />
              )}{' '}
              Delete everything
            </Button>
          )}
        </div>
        )}
        {/* FRONT-2594 Canvas: the panel is the card's LAST child so the chat's own
            children keep their place (and their state). Rendered, with its CSS,
            only while the panel is on screen — flag off or closed adds nothing. */}
        {isPanelVisible(canvas.layout) && (
          <>
            <style>{CANVAS_LAYOUT_CSS}</style>
            <CanvasPanel
              sessionId={inboxSessionId}
              items={canvas.items}
              activeKey={canvas.activeKey}
              view={canvas.view}
              onViewChange={canvas.setView}
              onSelect={canvas.select}
              onHide={() => canvas.setOpen(false)}
              onDirtyChange={onCanvasDirtyChange}
              flushRef={canvasFlushRef}
              switcherDot={canvas.panelUnseen}
            />
          </>
        )}
      </div>
    </div>

      {pendingWarning === 'graph' && (
        <InboxWarningDialog
          open
          warningKey="graph"
          title="Save this to your knowledge graph?"
          description="EDDY will extract the key entities and facts from this item and add them to your knowledge graph."
          confirmText="Save to graph"
          onResolve={resolveWarning}
        />
      )}
      {pendingWarning === 'delete' && (
        <InboxWarningDialog
          open
          warningKey="delete"
          title="Clear this chat?"
          description="This permanently clears the conversation for this item. The inbox item itself stays, and the asset remains open. This can't be undone."
          confirmText="Clear chat"
          destructive
          onResolve={resolveWarning}
        />
      )}
      {createPlanEnabled && isChat && (
        <CreatePlanDialog
          source={{ kind: 'chat', sessionId: inboxSessionId }}
          sourceLabel={chatItem?.title}
          open={createPlanOpen}
          onOpenChange={setCreatePlanOpen}
        />
      )}
      {createPlanEnabled && !isChat && footerPlanSource && (
        <CreatePlanDialog
          source={footerPlanSource.source}
          // FRONT-1107: the reader's own current name, so the dialog doesn't
          // say "from <old name>" straight after a rename here.
          sourceLabel={title}
          open={createPlanOpen}
          onOpenChange={setCreatePlanOpen}
        />
      )}
      {isChat && (
        <ChatRoleDialog
          sessionId={inboxSessionId}
          open={roleDialogOpen}
          onOpenChange={setRoleDialogOpen}
        />
      )}
    </CanvasContext.Provider>
  );
}

/**
 * The zen modal, plus the thread-switching the modal's own chat-activity menu
 * needs (FRONT-1337, reworked by FRONT-1579).
 *
 * This shell exists for one reason: switching threads has to give the body a
 * genuinely clean slate. Everything inside — the history query, the SSE read,
 * the composer draft, the voice binding — is bound to one session id, so the
 * original design swapped threads by changing the modal's mount key from the
 * home launcher. Only the launcher did that, which is exactly why the other six
 * doorways into a chat had no bar. Holding the chosen thread here reproduces
 * that clean remount (`key` on the body below) from inside the component, so no
 * caller has to know the feature exists.
 *
 * The `key` is load-bearing, not cosmetic: without it the body keeps state that
 * was initialised for the previous session at mount — most sharply the
 * removed-context-file set, whose lazy initialiser reads chat A's stored ids
 * and would then persist them back under chat B's key.
 *
 * The one-shot open props are deliberately dropped on a switch: they describe
 * how THIS chat was opened (the launcher's first message, the Work page's
 * container, an auto-started call), and replaying them onto a thread the user
 * merely hopped to would post a stray message or scope the wrong project.
 */
function InboxChatModalSwitchable(props: InboxChatModalProps) {
  const [switched, setSwitched] = useState<ModalAssetItem | null>(null);

  // Reset when the caller hands over a different asset (React's documented
  // adjust-state-during-render pattern, not an effect — an effect would render
  // one frame on the stale thread first). Every mount site keys this component
  // by item id, so a different id already remounts us; this covers a caller
  // that swaps the object while keeping the id (the Inspector's open-chat
  // request), which would otherwise stay stuck on the switched thread.
  const [lastItem, setLastItem] = useState(props.item);
  if (lastItem !== props.item) {
    setLastItem(props.item);
    if (switched) setSwitched(null);
  }

  const active = switched ?? props.item;

  return (
    <InboxChatModalInner
      {...props}
      {...(switched ? CLEARED_ON_SWITCH : null)}
      key={active.id}
      item={active}
      onSwitchThread={setSwitched}
    />
  );
}

/**
 * FRONT-1566: the header's "Role" control. Unlike every `HeaderIcon` around
 * it, this is a small LABELLED pill (icon + the word "Role"), not a bare
 * icon — deliberate, so it reads at a glance rather than needing a hover to
 * discover what it is. Styled on the always-visible Fast/Thoughtful chip
 * just above it (the `headerModelMode` span): quiet/outline when no role is
 * set, lit with the `primary` design token once one is — never a raw
 * Tailwind color scale (CLAUDE.md's color-coding rule).
 *
 * `errored` (adversarial-review finding 9): a failed read of this chat's
 * role leaves the query's `data` `undefined` — indistinguishable from "no
 * role set" by `hasRole` alone — so without this the pill claimed "Set a
 * role for this conversation" (a stated absence) for a conversation that
 * genuinely HAS one, purely because the GET failed. `errored` overrides
 * only the LABEL (an honest "couldn't check", never a false "not set"); the
 * quiet/outline styling is shared with the not-set state on purpose — this
 * is not a third color, just honest wording for a state we can't confirm
 * either way (clicking still opens the dialog, which shows its own error).
 */
function ChatRoleButton({
  hasRole,
  errored,
  onClick,
}: {
  hasRole: boolean;
  errored?: boolean;
  onClick: () => void;
}) {
  let label = 'Set a role for this conversation';
  if (hasRole) label = 'This conversation has a role set — click to edit';
  if (errored) label = "Couldn't check this conversation's role — click to try again";
  return (
    <Tip label={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        data-testid="chat-role-button"
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[length:var(--text-xs)] font-medium whitespace-nowrap transition-colors ${
          hasRole && !errored
            ? 'border-primary/25 bg-primary/10 text-primary hover:bg-primary/15'
            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <IdentificationBadge size={11} weight={hasRole && !errored ? 'fill' : 'regular'} />
        Role
      </button>
    </Tip>
  );
}

function HeaderIcon({
  icon: Icon,
  title,
  onClick,
  hide,
  destructive,
}: {
  icon: typeof CalendarPlus;
  title: string;
  onClick: () => void;
  hide?: boolean;
  /** Destructive hover styling (FRONT-861) — matches the row-level trash icons. */
  destructive?: boolean;
}) {
  if (hide) return null;
  return (
    <Tip label={title}>
      <button
        type="button"
        onClick={onClick}
        aria-label={title}
        className={`size-8 inline-flex items-center justify-center rounded-[10px] transition-colors ${
          destructive
            ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
      >
        <Icon size={15} weight="regular" />
      </button>
    </Tip>
  );
}

/**
 * FRONT-3146: the chat / document / file window. Inside the app shell it is a
 * window in the shared layer (docked beside at most one other, minimizable into
 * the window bar); anywhere else it is the full-screen overlay it was before.
 *
 * Two kinds of owner. The window host (`features/windows/window-host.tsx`)
 * passes `windowId` — the window already exists in the store. Every other mount
 * (the Files page's viewer, the Work board, the Chats page ...) is page-local:
 * it registers its own window under a private id, so it takes part in the
 * two-window limit and the bar, and a bar close or a Cancel in the chooser
 * calls its `onClose`. Its window ends with the page, as the overlay always did.
 * Pending (third window, waiting for the choice) nothing is mounted, so a first
 * message is never sent before the window is visible.
 */
export function InboxChatModal(props: InboxChatModalProps) {
  const localId = `local-chat:${useId()}`;
  const hosted = props.windowId !== undefined;
  return (
    <WindowShell
      id={props.windowId ?? localId}
      kind="chat"
      refId={props.item.id}
      title={assetWindowTitle(props.item)}
      hosted={hosted}
      onDismiss={props.onClose}
      onClose={props.onClose}
    >
      <InboxChatModalSwitchable {...props} />
    </WindowShell>
  );
}
